// Thin client for the GGPathOS api auth surface. Every call is credentialed (`credentials:
// 'include'`) so the httpOnly session cookie is sent — the token never lives in JS. The magic link
// itself is handled server-side (the api consumes it and 302s back here), so there is no
// verify call in the SPA.
import { apiBaseUrl } from './config.js';

/**
 * Paths that must never trigger the refresh-and-replay below. `/auth/refresh` is the refresh itself
 * (infinite recursion); the rest are the unauthenticated entry points, where a 401 is the real answer
 * and rotating the chain would be meaningless.
 */
const NO_REFRESH_PATHS: ReadonlySet<string> = new Set([
  '/auth/refresh',
  '/auth/verify',
  '/auth/magic-link',
  '/auth/logout',
]);

/**
 * The single in-flight refresh, shared by every 401 that arrives while it runs.
 *
 * This dedupe is load-bearing, not an optimization: refresh-token rotation is SINGLE-USE server-side,
 * and replaying an already-consumed token is treated as a theft signal that revokes the entire chain
 * (`rotateRefreshToken` → `revokeChain`). So two concurrent `POST /auth/refresh` calls would sign the
 * user out. One rotation, N awaiters.
 */
let inFlightRefresh: Promise<boolean> | null = null;

/** Rotate the session from the httpOnly refresh cookie. Resolves true iff a fresh access token was set. */
function refreshSession(): Promise<boolean> {
  if (!inFlightRefresh) {
    const run = (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        return res.ok;
      } catch {
        // Offline / CORS / DNS — indistinguishable here from a rejected refresh, and equally fatal
        // to the retry. Fall through as "could not refresh" and let the original 401 stand.
        return false;
      }
    })();
    inFlightRefresh = run;
    // Clear only if we're still the current attempt, so a later refresh isn't cleared by an earlier
    // one settling. (Assign before wiring the reset: a synchronous throw would otherwise null the
    // slot before the assignment lands and strand a permanently-resolved promise there.)
    void run.finally(() => {
      if (inFlightRefresh === run) inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
}

/**
 * Credentialed fetch that transparently renews a lapsed session.
 *
 * The access token lives 15 minutes; the refresh chain lives an hour (sliding, 8-hour ceiling). Without
 * this, the app's usable session was the ACCESS token's 15 minutes — every call after that 401'd and
 * bounced the user to /sign-in even though a perfectly good refresh cookie sat in the browser.
 *
 * On a 401 we rotate once and replay once. Replaying is safe even for a non-idempotent POST: a 401 is
 * raised by `requireAuth` as a preHandler, so the route body never ran. (All bodies here are strings,
 * so re-sending `init` is a plain re-read — nothing is a consumed stream.)
 */
async function request(path: string, init?: RequestInit): Promise<Response> {
  // `content-type: application/json` ONLY when there is a body to describe. Sending it on a bodyless
  // POST makes Fastify's JSON parser reject the empty body with a 400 ("Body cannot be empty when
  // content-type is set to 'application/json'") — raised during parsing, BEFORE requireAuth runs, so
  // even a perfectly valid session fails. That silently broke every bodyless POST: /auth/logout (which
  // therefore never cleared cookies or revoked the refresh chain), /analyses/:id/cancel, and
  // /auth/mcp-token. `server.inject()` sends no content-type, so only a browser reproduces it.
  const hasBody = init?.body !== undefined && init.body !== null;
  const send = (): Promise<Response> =>
    fetch(`${apiBaseUrl}${path}`, {
      credentials: 'include',
      ...init,
      headers: {
        ...(hasBody ? { 'content-type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
    });

  const res = await send();
  if (res.status !== 401 || NO_REFRESH_PATHS.has(path)) return res;

  const refreshed = await refreshSession();
  // Refresh declined (chain expired past its hour, revoked, or membership pulled) — the session is
  // genuinely gone. Hand the original 401 back so callers route to sign-in.
  if (!refreshed) return res;
  return send();
}

/** The verified access-token claims echoed by GET /auth/session. Shape kept permissive on purpose. */
export type SessionClaims = Record<string, unknown>;

export interface Membership {
  id: string;
  userId: string;
}

export interface MembershipsResponse {
  tenantId?: string;
  memberships: Membership[];
}

/**
 * Request a magic link. The api ALWAYS replies 202 (no account enumeration), so success here just
 * means "we asked" — the UI shows the same "check your email" either way.
 */
export async function requestMagicLink(
  email: string,
  tenantSlug: string,
  returnPath?: string,
): Promise<void> {
  await request('/auth/magic-link', {
    method: 'POST',
    // Omitted entirely when absent — the api treats a missing claim as "land at `/`", which is what
    // every client did before deep links survived sign-in (#968).
    body: JSON.stringify({ email, tenantSlug, ...(returnPath ? { returnPath } : {}) }),
  });
}

/**
 * Confirm a magic-link sign-in (POST-to-confirm hardening). The clicked email link now lands on the
 * SPA confirm page; this POSTs the token to consume it and mint the session. Because a mail scanner's
 * passive GET/HEAD pre-fetch of the link never reaches this POST, it can no longer burn the single-use
 * token. On failure the `reason` (e.g. `link_invalid`, `forbidden`) drives the sign-in error message.
 */
export async function confirmSignIn(
  token: string,
): Promise<{ ok: boolean; reason?: string; returnPath?: string }> {
  const res = await request('/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  if (res.ok) {
    // Where the person was trying to go before the session lapsed (#968). UNVALIDATED here — the
    // caller runs it through `safeReturnPath` before navigating. Absent on an older token, and
    // absent when nothing was captured.
    try {
      const body = (await res.json()) as { returnPath?: string };
      return typeof body.returnPath === 'string'
        ? { ok: true, returnPath: body.returnPath }
        : { ok: true };
    } catch {
      // A 200 with no/!JSON body is still a successful sign-in; it just carries no destination.
      return { ok: true };
    }
  }
  let reason: string | undefined;
  try {
    reason = ((await res.json()) as { reason?: string }).reason;
  } catch {
    // Non-JSON error (e.g. a rate-limit body) — leave reason undefined → generic message.
  }
  return reason ? { ok: false, reason } : { ok: false };
}

/** The current session claims, or null when unauthenticated (401). */
export async function getSession(): Promise<SessionClaims | null> {
  const res = await request('/auth/session');
  if (!res.ok) return null;
  return (await res.json()) as SessionClaims;
}

/** Memberships visible inside the request's tenant GUC scope — the per-request RLS proof. */
export async function getMemberships(): Promise<MembershipsResponse | null> {
  const res = await request('/auth/memberships');
  if (!res.ok) return null;
  return (await res.json()) as MembershipsResponse;
}

/** Revoke the session server-side and clear cookies. */
export async function logout(): Promise<void> {
  await request('/auth/logout', { method: 'POST' });
}

/** A minted long-lived MCP token (POST /auth/mcp-token, ADR-0030). */
export interface McpToken {
  token: string;
  tokenType: string;
  expiresInSec: number;
  expiresAt: string;
}

/** Why a mint failed. Distinguishing these is the difference between "sign in again" and "try again". */
export type McpTokenFailure =
  'unauthenticated' | 'forbidden' | 'rate_limited' | 'unavailable' | 'server_error';

export type McpTokenResult = { ok: true; token: McpToken } | { ok: false; reason: McpTokenFailure };

/**
 * Mint a long-lived MCP token to paste into an MCP client (Claude Code). Uses the same credentialed
 * request as every other authed call, so it works where a raw console fetch (blocked by extensions/CSP)
 * does not — and so a lapsed access token is silently renewed rather than surfaced as a failure.
 *
 * Returns a discriminated result rather than null: collapsing every non-OK into one value made the UI
 * blame an expired session for what might be a 500 or a rate limit. `unauthenticated` here means the
 * refresh ALSO failed — the session is really gone.
 */
export async function createMcpToken(): Promise<McpTokenResult> {
  let res: Response;
  try {
    res = await request('/auth/mcp-token', { method: 'POST' });
  } catch {
    // fetch rejects (not resolves) on network/CORS failure. Previously this escaped the caller as an
    // unhandled rejection and left the panel stuck on "Generating…".
    return { ok: false, reason: 'server_error' };
  }
  if (res.ok) return { ok: true, token: (await res.json()) as McpToken };
  if (res.status === 401) return { ok: false, reason: 'unauthenticated' };
  // 403 = the session is fine, the `can_use_mcp` capability is not granted (ADR-0033 D1). Telling the
  // user to sign in again would send them round a loop that cannot succeed.
  if (res.status === 403) return { ok: false, reason: 'forbidden' };
  if (res.status === 429) return { ok: false, reason: 'rate_limited' };
  if (res.status === 503) return { ok: false, reason: 'unavailable' };
  return { ok: false, reason: 'server_error' };
}

// ── Premium Deal Analyzer (authed) ──────────────────────────────────────────
// POST /analyses enqueues a model-judged run and returns its id; GET /analyses/:id polls it. The
// numbers in the bundle are engine-owned (code), the prose is model-owned — see packages/agents/
// deal-analyzer. These view-types are deliberately PERMISSIVE (everything nullable/optional): the
// web package is workspace-isolated (no @ggpathos/* imports), and the model-authored narrative
// modules can be absent, so the renderer must never assume a field is present.

/** The deal a user submits — mirrors @ggpathos/engine `DealInputs`; every field optional. */
export interface DealInputs {
  noi?: number | null;
  annualDebtService?: number | null;
  totalCost?: number | null;
  loanAmount?: number | null;
  exitCapRate?: number | null;
  vacancyRate?: number | null;
  expenseRatio?: number | null;
  potentialBaseRent?: number | null;
  miscIncome?: number | null;
  totalOpex?: number | null;
  totalUnits?: number | null;
  avgRent?: number | null;
  salePrice?: number | null;
  holdYears?: number | null;
  constructionMonths?: number | null;
  isLIHTC?: boolean;
  assetClass?: string | null;
  propertyClass?: string | null;
  marketComps?: { marketWideConcessionRate?: number | null } | null;
}

// `awaiting_index` is a real in-flight state the api returns for an ingestion run parked while Nexus
// finishes OCR/embedding (ADR-0020 Phase 2). The poll loop treats it as in-flight (like queued/running).
export type AnalysisStatus =
  'queued' | 'running' | 'awaiting_index' | 'succeeded' | 'failed' | 'canceled';

/**
 * How far along a live run is — the worker's checkpoints, IN ORDER (#1400, migration 0063).
 *
 * ⚠️ A HAND-KEPT MIRROR of `ANALYSIS_PROGRESS_STAGES` in `@ggpathos/db` (schema/analyses.ts): the web
 * package depends on no workspace package, so it cannot import it. The DB's CHECK refuses any other
 * spelling, so the server cannot send one — but a NEW stage added server-side before this list learns
 * it would arrive unknown, and the screen treats an unknown stage as no stage at all (status + elapsed),
 * never as a step it cannot place.
 */
export const ANALYSIS_PROGRESS_STAGES = [
  'documents',
  'extraction',
  'proforma',
  'underwrite',
  'findings',
] as const;
export type AnalysisProgressStage = (typeof ANALYSIS_PROGRESS_STAGES)[number];

/** The last checkpoint a LIVE run passed, and when. The server sends `null` once the run has ended. */
export interface AnalysisProgress {
  /** Typed as `string`, not the union: it is the SERVER's word, and an unknown one must be tolerable. */
  stage: string;
  /** ISO 8601 — when the worker passed that checkpoint. */
  at: string;
}
export type Verdict = 'READY' | 'ADVANCE' | 'WORKSHOP' | 'RESUBMIT';
export type MatrixBand = 'strong' | 'adequate' | 'marginal' | 'weak' | 'unsolved';

export type CategoryKey =
  | 'locationMarket'
  | 'financialUnderwriting'
  | 'sponsorExecution'
  | 'capitalStack'
  | 'productDesign'
  | 'exitStrategy';

/**
 * Canon's ruled set (ADR-0035 §9) — `FIXABLE|STRUCTURAL|N/A`.
 *
 * The union is widened with `(string & {})` on purpose: the wire is untyped, and analyses persisted
 * before ADR-0035 carry the legacy `fixable|partial|structural` in their stored jsonb bundle. Narrowing
 * to the canon three would be a lie the compiler believes — the renderer handles both
 * (`FIXABILITY_VIEW`).
 */
export type Fixability = 'FIXABLE' | 'STRUCTURAL' | 'N/A' | (string & {});

/**
 * A category's full detail (engine ADR-0031 `category-detail-fields`). The engine emits this per
 * category on current analyses; the narrative fields can be empty strings when the model had nothing to
 * add. `potentialScore` is the achievable ceiling if the data gap were closed.
 */
export interface CategoryDetail {
  score: number;
  anchorUsed?: string;
  fixability?: Fixability;
  notes?: string;
  pathToImprovement?: string;
  potentialScore?: number;
}

/** Weight partitioned by whether each category's tier rests on evidence. Mirrors `@ggpathos/engine`. */
export interface EvidenceCoverage {
  evidenced: number;
  unevidenced: number;
  untracked: number;
  total: number;
}

export interface InvestmentScore {
  /**
   * Per category, EITHER a bare number (historical analyses, pre-ADR-0031) OR the full `CategoryDetail`
   * object (current analyses). The renderer reads both — a stored analysis is never migrated in place, so
   * the UI must keep drawing the old shape too.
   */
  categoryScores?: Partial<Record<CategoryKey, number | CategoryDetail>>;
  rawFinancialUnderwriting?: number;
  categoryTotal?: number;
  structuralPenalty?: number;
  overallScore?: number;
  /**
   * The engine-composed POTENTIAL score (canon v2.7.16 step 11): the category potentials plus the potential
   * penalty, 0–100. Absent on analyses scored before step 11 was built, and on the free tier (paid field).
   */
  potentialScore?: number;
  /** The AS_PRICED relief claim as the engine ruled it; `accepted` is what moved the potential. */
  potentialRelief?: { applies: boolean; evidence: string; accepted: boolean };
  /** Canonical headline (canon §4.5). Current analyses carry this. */
  recommendation?: Verdict;
  /**
   * @deprecated Historical alias of {@link recommendation}. A stored analysis is never migrated in
   * place, so pre-drop records carry `verdict` and no `recommendation` — the UI must read
   * `recommendation ?? verdict` forever. New analyses (post ADR-0035 §9) carry only `recommendation`.
   */
  verdict?: Verdict;
  structural?: boolean;
  /**
   * The band the SCORE alone would have earned, present only when the structural ceiling lowered the
   * recommendation (ADR-0039). Absent on every analysis the ceiling did not bite, and on every analysis
   * stored before the field existed — absent means "not capped", never "unknown".
   */
  recommendationCappedFrom?: Verdict;
  /**
   * ADR-0069 N20 — how far the score sits from the line that decides its recommendation, or that the
   * structural ceiling held it. Absent on a scorecard stored before N20; the report then shows no
   * margin rather than computing one in the browser.
   */
  recommendationMargin?: RecommendationMarginView;
  /** ADR-0069 N20 — present only when a borderline band fired. Annotation only. */
  borderline?: BorderlineView;
}

/** Engine `RecommendationMargin`, as the SPA receives it. */
export type RecommendationMarginView =
  | {
      kind: 'margin';
      recommendation: Verdict;
      line: number;
      lineBand: Verdict;
      side: 'above' | 'below';
      points: number;
    }
  | {
      kind: 'held';
      recommendation: Verdict;
      cappedFrom: Verdict;
      reason: string;
    };

/**
 * Engine `BorderlineAnnotation`, as the SPA receives it. `tierBoundary` is PAID whole: the free tier
 * never receives it (any part of it places the paid dev spread — `entitlements.ts`), and a free
 * scorecard whose only flag was a tier flag arrives with no `borderline` at all.
 */
export interface BorderlineView {
  recommendationLine?: {
    line: number;
    distance: number;
    recommendationIfCrossed: Verdict;
  };
  tierBoundary?: {
    boundaryBps: number;
    devSpreadBps: number;
    distanceBps: number;
    swingPoints: number;
    scoreIfCrossed: number;
    recommendationIfCrossed: Verdict;
    structuralCeiling?: 'sets' | 'clears';
  };
}

export interface IrrMatrixCell {
  rentDelta: number;
  exitCap: number;
  projectIRR: number | null;
  band: MatrixBand;
  isBaseCase: boolean;
}

export interface IrrMatrix {
  rentDeltas: number[];
  exitCaps: number[];
  rows: IrrMatrixCell[][];
}

/**
 * Where the deal's own rent sits against its class comp band (engine `rentPosition`).
 *
 * ⚠️ `basis` is not metadata — it is part of the claim. On 14 of 75 stored deals the rent is DERIVED
 * from gross potential rent rather than stated, and a GPR figure assumes full occupancy with no
 * concessions. Rendering the number without the basis would present a derivation as a document fact.
 */
export interface RentPositionView {
  canonicalClass?: string;
  bandLow?: number | null;
  bandHigh?: number | null;
  dealRent?: number | null;
  basis?: 'stated' | 'derived-from-gpr';
  standing?: 'above' | 'within' | 'below';
  outsideBy?: number | null;
  /** Market-wide concession rate as a ratio; null when the comps stated none. */
  concessionRate?: number | null;
}

/** One CSI division as the engine classified it. */
export interface ConstructionDivision {
  code?: string;
  name?: string;
  dollars?: number | null;
  /** Division dollars ÷ total GSF. */
  perGSF?: number | null;
  /** Share of the division sum (NOT of the GMP — the sum excludes omissions and loading). */
  share?: number | null;
  /**
   * The benchmark verdict — **absent on Division 01 only**, until `#570` closes.
   *
   * ⚠️ Absent means WITHHELD, not "no opinion", and the renderer must say which. The projection
   * (`publishedConstruction`) drops these keys for Division 01 rather than trusting a surface to
   * remember, so a missing verdict here is always the deliberate one.
   */
  status?: string;
  direction?: string;
  /**
   * `above` / `below` / `within` the PRINTED band, tolerance-free — served by the engine so no
   * surface has to re-derive it.
   *
   * ⚠️ **Optional because analyses persisted before 2026-08-26 do not carry it.** `analyses.result`
   * is a stored snapshot, so an old run renders through the local fallback in `ConstructionCard`
   * rather than regressing to a bare "In band".
   */
  bandPosition?: string;
  adjLow?: number | null;
  adjHigh?: number | null;
}

/** What the division benchmarking actually did — never a bare "withheld" flag. */
export interface ConstructionBenchmarking {
  /**
   * True when the verdicts may NOT be read as findings: below 95% of canon's classified weight, or on
   * a proxy GSF basis, the denominator is incomplete (canon `intel-v2.7.6`). The bands do not move —
   * renormalising would grade a budget on a curve set by whatever the sponsor happened to classify.
   */
  provisional?: boolean;
  provisionalReasons?: string[];
  classifiedWeight?: number | null;
  division01Withheld?: boolean;
}

/**
 * The CSI cost decomposition (engine `csiDecomposition`), as the report renders it.
 *
 * The per-division verdicts were omitted here while the Intelligence Workbook's $/GSF bands disagreed
 * with each other by up to 60% (#479) — a property of the spec, not of any budget. **#479 closed
 * 2026-08-08**, canon corrected the bands, and the Consumer Register released them: _"the rest of the
 * per-division panel un-withholds by ruling of August 10, 2026"_.
 *
 * ⚠️ `counts` is STILL absent, and Division 01's verdict with it. An OUTSIDE_RANGE tally would
 * re-publish the withheld division in aggregate, which is the same disclosure by another route.
 */
export interface ConstructionDecomposition {
  productType?: string;
  totalGSF?: number | null;
  divisions?: ConstructionDivision[] | null;
  /** Sum of all classified division dollars. */
  divisionSum?: number | null;
  /** A single division over 50% of the sum — likely miscategorized, and a coverage check, not a cost one. */
  lumpedDivision?: string | null;
  reconcile?: {
    status?: string;
    /** The GMP / loaded hard-cost total from the estimate. */
    hardCosts?: number | null;
    /** (hardCosts − accountedTotal) ÷ hardCosts — contractor loading. */
    gap?: number | null;
    /** Real construction cost the extraction declined to attribute to one division. */
    omittedDollars?: number | null;
    /** `divisionSum + omittedDollars` — every base construction dollar accounted for. */
    accountedTotal?: number | null;
  } | null;
  /** Regional cost index. Feeds the benchmark bands ONLY, so it is reported as provenance, not a figure. */
  regional?: { multiplier?: number | null; basis?: string; gap?: boolean } | null;
  /** What the benchmarking did — provisionality, its reasons, and the Division 01 withholding. */
  benchmarking?: ConstructionBenchmarking | null;
}

/**
 * CSI scope coverage (ADR-0069 A11): which divisions canon expects for this product type that the
 * estimate carries no line for. Paid only, because the free tier's allowlist never names it. Absent on
 * runs without a decomposed budget and on every run that predates the field.
 */
export interface ScopeCoverageView {
  /** Canon weight of the divisions with NO line: the headline. */
  missingWeight?: number;
  /**
   * Heaviest first. ⚠️ The engine's per-gap `severity` is deliberately NOT typed here: its cutoffs are
   * our own unmeasured starting values, not canon, and the report does not show them.
   */
  gaps?: {
    code: string;
    name: string;
    /** Canon's expected budget share for this division and product type (sheet 3). */
    expectedWeight: number;
  }[];
  /** Divisions priced as one un-itemized line the lumping cap dropped: covered, not tabled. */
  cappedCodes?: string[];
  /** Base dollars outside every itemized division; above 0, a gap may be priced inside them. */
  unattributedDollars?: number;
}

/**
 * One value-creation lever the Deal Mechanic evaluated (engine `deal-mechanic.ts`, module 6).
 *
 * ⚠️ `status` is the field that matters, and it is NOT decoration. `quantified` is the only rung
 * carrying real bps; `no_delta` means the lever was tested and there is nothing to capture;
 * `verify` means a validation override fired (a rent push above the class band ceiling, say); and
 * `insufficient_data` means the inputs to test it were missing; `withheld` means the engine declined
 * to size it on grounds other than a missing input (a contradicted unit count, a class from the deal's
 * own rent). Rendering only the quantified levers would let "we tested this and it is already optimal" and "we could not test this" both read as
 * silence — the same defect `rentPositionUnavailable` exists to stop one panel down.
 */
export interface LeverImpact {
  leverType: 'rent_push' | 'opex_reduction' | 'ancillary_revenue' | 'debt_resize';
  label: string;
  status: 'quantified' | 'no_delta' | 'verify' | 'insufficient_data' | 'withheld';
  /** Yield-on-cost impact in basis points (NOIadd / TDC × 10000). */
  yocBps?: number | null;
  /** Project-IRR impact in basis points (yocBps × the class leverage proxy). */
  irrBps?: number | null;
  /** Current value as resolved — rent $/unit/mo, opex ratio, or a rate. */
  current?: number | null;
  /** Target value, after any band clamp. */
  target?: number | null;
  /** Incremental stabilized NOI the lever adds, whole dollars. */
  noiAdd?: number | null;
  /** Why it could not quantify, or which clamp/override applied. */
  note?: string | null;
}

/**
 * The Deal Mechanic — prescriptive value-creation levers (module 6, workbook sheet `9_DealMechanic`).
 *
 * Where the Scorecard GRADES the deal, this says what to do about it: each lever's basis-point impact
 * on YOC and Project IRR, ranked against the gap to a target IRR ("Move the Needle").
 *
 * ⚠️ All lever math is deterministic — the workbook's hard rule is that JS owns the bps and
 * model-stated numbers are overridden. Nothing here is a model opinion.
 *
 * Null when no canonical class resolves, which is also exactly when `classUnavailable` is populated —
 * so an absent panel has a stated reason rather than reading as "there is nothing to do here".
 */
export interface DealMechanicResult {
  levers?: LeverImpact[] | null;
  /** The top 3 quantified levers by IRR bps — the "Move the Needle" set. */
  rankedTop3?: LeverImpact[] | null;
  /** Stacked YOC / IRR bps across the top-3. */
  stackedYocBps?: number | null;
  stackedIrrBps?: number | null;
  /** Base-case Project IRR as a ratio, or null when the returns engine could not compute one. */
  currentIRR?: number | null;
  targetIRR?: number | null;
  /** (target − current) in bps; POSITIVE means still short of the hurdle. Null if no IRR. */
  gapBpsToTarget?: number | null;
  /** True when the stacked top-3 closes the gap to target. */
  closesGap?: boolean | null;
}

/**
 * Why no canonical class could be resolved — present only when `classCalibration` is null, which is
 * also when `dealMechanic` is null.
 *
 * ⚠️ `absent` and `unmatched` are different facts and the distinction is the whole value of the
 * record: "nobody stated a class" versus "something was stated that carries no class letter"
 * (`assetClass: "Multifamily"` is a product type, not a class). Only the second is a reason to go
 * back and look at the document.
 */
export interface ClassSignalState {
  signal: 'propertyClass' | 'reviewerContext' | 'assetClass' | 'rentPSF';
  state: 'absent' | 'unmatched';
  /** What was stated, when something was. Never invented; omitted for `absent`. */
  stated?: string;
}

/** The rendered subset of the premium bundle (PremiumDealAnalysis). Permissive by design. */
export interface PremiumDealAnalysis {
  tier?: 'premium';
  deterministic?: {
    spine?: {
      /**
       * Metrics the engine REFUSED to compute because the document contradicts itself about one of
       * their inputs (ADR-0043 R1 + D6).
       *
       * 🔴 NOT THE SAME AS A NULL METRIC, and the whole reason this field exists. A withheld
       * `costPerDoor` arrives as `null` like any other, so without this the report renders an
       * em-dash indistinguishable from "we could not compute it" — when what actually happened is
       * that the workbook stated two different unit counts and the engine declined to divide by
       * either. `self-consistency.ts` is explicit that silence is its own kind of dishonesty.
       */
      withheld?: { key?: string; field?: string; reason?: string }[] | null;
      dscr?: { value?: number | null } | null;
      yieldOnCostPct?: number | null;
      devSpreadBps?: number | null;
      costPerDoor?: number | null;
      exitValue?: number | null;
      ltc?: number | null;
      debtYield?: number | null;
      refiLTV?: number | null;
      returns?: {
        projectIRR?: number | null;
        equityMultiple?: number | null;
        equity?: number;
        holdYears?: number;
        band?: string;
        loanFromStatedEquity?: boolean;
      } | null;
    } | null;
    sensitivity?: { matrix?: IrrMatrix | null } | null;
    lender?: LenderSummary | null;
    dataQuality?: DataQualityReport | null;
    construction?: ConstructionDecomposition | null;
    /** ADR-0069 A11: present beside `construction`, paid only. */
    scopeCoverage?: ScopeCoverageView | null;
    /**
     * The prescriptive lever analysis (module 6). Computed on EVERY run since the module shipped and
     * persisted into every `analyses.result` — it was simply never rendered, so the report graded the
     * deal without ever saying what would change it. Absent on runs that predate the field.
     */
    dealMechanic?: DealMechanicResult | null;
    /**
     * WHY no canonical class resolved, when `classCalibration` and `dealMechanic` are both null.
     * Read it before concluding an absent lever panel means the deal has no levers.
     */
    classUnavailable?: ClassSignalState[] | null;
    rentPosition?: RentPositionView | null;
    /**
     * WHY no rent position could be established, when `rentPosition` is null (engine
     * `explainMissingRentPosition`). One of `no-class` | `no-comps` | `unusable-band` | `no-deal-rent`.
     *
     * Read it BEFORE concluding anything from an absent rent card. The engine's own note is that
     * "silence would let a reader take 'no rent finding' for 'the rent was checked and was fine'" —
     * this is what stops the report doing exactly that. Absent on runs that predate the field.
     */
    rentPositionUnavailable?: string | null;
    /**
     * ADR-0069 N20 — the CBRE exit-cap REFERENCE for a deal that states no exit cap, or why none was
     * shown. Never fed a score. A free tenant receives `kind`/`label`/`reason`/`market` only; the
     * figures (`capRangeBps`, `exitValueRange`, the reference `sentence`) are paid.
     */
    exitCapReference?: ExitCapReferenceView | null;
  } | null;
  /**
   * Modules that were asked to run and failed. Empty/absent on a clean run.
   *
   * Read this BEFORE concluding anything from an absent section. Per-module isolation means a narrative
   * module can fail without failing the run — so `icMemo: null` means either "never requested" or "the
   * reasoner threw", and only this field tells them apart. Without it the Deal Room renders a missing
   * section with no explanation, which trades a loud failure for a silent one.
   */
  moduleFailures?: { module: string; reason?: string }[] | null;
  /**
   * ADR-0069 N20 — the narrative-number guard: an exit figure our own prose states that sits 1.5% to
   * 15% off the engine's exit value. Flag-only; it moved no score or number. Absent when nothing was
   * flagged and on runs that predate the guard.
   */
  narrativeNumbers?: NarrativeNumberFlagView[] | null;
  scorecard?: {
    score?: InvestmentScore;
    narrative?: string;
    model?: string;
  } | null;
  /**
   * How much of the 100 points rests on a recorded basis rather than a midpoint default (#451).
   * Computed server-side from the ADR-0037 A validation audit — the rule for what counts as evidence
   * is policy and stays in the engine. Absent on runs whose bundle carries no audit.
   */
  evidenceCoverage?: EvidenceCoverage | null;
  sensitivity?: { narrative?: string; matrix?: IrrMatrix | null } | null;
  lender?: (LenderSummary & { narrative?: string }) | null;
  /**
   * The model-judged Construction Risk read. The DECOMPOSITION is deterministic and rides
   * `deterministic.construction`; this carries only the narrative over it, plus which model wrote it.
   * `model: 'none'` means the module ran and had no budget to read — which it then says in words.
   */
  construction?: { narrative?: string; model?: string } | null;
  sponsor?: {
    score?: { score?: number };
    namedPrincipals?: string;
    gcRelationship?: string;
    narrative?: string;
  } | null;
  icMemo?: {
    /** Canonical headline (canon §4.5); historical records carry `verdict`. Read `recommendation ?? verdict`. */
    recommendation?: Verdict;
    /** @deprecated Historical alias of {@link recommendation}. */
    verdict?: Verdict;
    overallScore?: number;
    recommendationTier?: string;
    /** Canon's exact wording for the tier. Absent on runs analysed before it was carried on the wire. */
    recommendationTierLabel?: string;
    narrative?: string;
  } | null;
  /**
   * The canon scoring standard this run was scored under (ADR-0038 F0/R7). Optional because every run
   * persisted before the stamp legitimately carries none — the UI shows it when it exists rather than
   * asserting a version the scorecard was never scored under.
   */
  scoringStandardVersion?: string;
  /**
   * Which external reference-data checks this run had wired. `[]` means none ran — the manual form path
   * runs no external verification at all. **Absent** means the run predates the field, which is NOT the
   * same as `[]` and must not be rendered as "none".
   */
  externalChecks?: readonly string[];
  generatedAt?: string;
}

export interface LenderSummary {
  isLIHTC?: boolean;
  dscr?: number | null;
  dscrFloor?: number;
  /**
   * Where `dscrFloor` came from (ADR-0069 N21) — `label` is plain-language provenance for the report.
   * Absent on runs analysed before the field existed; show the floor alone then.
   */
  dscrFloorBasis?: {
    floor: number;
    basis: 'state-agency' | 'national-default' | 'market-rate';
    label: string;
    /** Informational context on the floor (today: Indiana's published coverage ranges); never moves it. */
    note?: string;
  };
  dscrPass?: boolean | null;
  /** #1575 — the pass rests on a withheld replacement reserve, so it is UNVERIFIED. */
  dscrReserveWithheld?: true;
  debtYield?: number | null;
  constructionLTC?: number | null;
  ltcCap?: number;
  ltcPass?: boolean | null;
  refiLTV?: number | null;
  stabilizedValue?: number | null;
  /**
   * Which loan facility the loan-derived figures rest on (#438). `'unqualified'` means one
   * `loanAmount` fed both the construction view (LTC) and the takeout view (debt yield, refi LTV),
   * so at most one of them was measured against its own facility. Absent on records written before
   * the disclosure shipped — treat absent as "not declared", never as "qualified".
   */
  loanBasis?: 'unqualified' | null;
}

/** One narrative-number flag (ADR-0069 N20). `field` is the bundle path of the text it was found in. */
export interface NarrativeNumberFlagView {
  field: string;
  severity: 'reconcile';
  /** States both figures, so the Hub renders the sentence and reads no figure off the flag. */
  message: string;
}

export interface DataQualityReport {
  score?: number;
  badge?: 'high' | 'medium' | 'low';
  fieldCompleteness?: number;
  presentFields?: string[];
  missingFields?: string[];
  flags?: { field: string; severity: 'material' | 'reconcile' | 'info'; message: string }[];
  summary?: string;
}

/** One field the extractor pulled from the document package, with its source + confidence. */
export interface ExtractionProvenance {
  field: string;
  sourceDoc?: string;
  confidence?: string;
  note?: string;
}

/** One located field of the deterministic workbook read, as persisted (ADR-0042 D5 provenance). */
export interface SpineField {
  value?: number | string | null;
  /** `template-nr` | `label-matched` | `derived` | `not-found`. */
  source?: string;
  confidence?: string;
  cell?: string | null;
  note?: string;
}

/**
 * The deterministic read of the proforma workbook that sits behind the figures (ADR-0042).
 *
 * `fields` carries each located value WITH its cell, which is what lets a disputed figure be answered
 * with `Assumptions!C14` rather than "the model read it".
 */
export interface ProformaSpineRead {
  /** The workbook the spine was read from, so a disputed figure names its source document. */
  filename?: string;
  /**
   * Where that workbook's bytes came from — `upload` (a human sent them) or `connector` (pulled back
   * out of Nexus on the autonomous path). Absent on runs written before the spine could reach
   * anything but an upload, and rendered as nothing in that case rather than guessed at.
   */
  bytesFrom?: 'upload' | 'connector';
  /** Whether the GGP template's named ranges matched. The override runs on this path ONLY. */
  isTemplate?: boolean;
  fieldsLocated?: number;
  fields?: Record<string, SpineField>;
  /**
   * Other GGP-template workbooks in this project that parsed and were NOT read (#653).
   *
   * Present only when there was more than one, so its presence is itself the finding. A reader who
   * sees one filename beside a figure has no way to know a second proforma existed — which is exactly
   * how The Commons underwrote from a superseded workbook for twelve runs without anyone noticing.
   */
  alsoPresent?: { filename: string; ingestedAt?: string; documentId?: string }[];
  /**
   * Rival templates that state DIFFERENT scored figures (ADR-0069 N32). Without a governing pin that
   * applied, no workbook figure was applied to the score: which file governs is a person's call.
   */
  rivalsDisagree?: {
    filename: string;
    fields: string[];
    documentId?: string;
    /** Filed after the governing pin was set (N32). Non-blocking; `ingestedAt` says when. */
    newerThanPin?: true;
    ingestedAt?: string;
  }[];
  /** The Nexus document id of the workbook read — what a governing pin names (ADR-0069 N32). */
  documentId?: string;
  /** The record's governing-proforma pin and what became of it on this run (ADR-0069 N32). */
  governing?: {
    documentId: string;
    recordedByName: string | null;
    recordedAt: string;
    applied: boolean;
    reason?:
      | 'not-on-record'
      | 'not-read'
      | 'upload-unidentified'
      | 'not-a-template'
      | 'not-a-workbook'
      | 'withdrawn';
    filename?: string;
  };
  /**
   * Which GGP template the workbook is (ADR-0069 N31): its structural family, and the version its README
   * states — which drifts, so it is shown as "says" and never trusted alone.
   */
  template?: { family: string; stated: string | null };
  /** Known template formula defects found in this workbook (ADR-0069 N31). Absent when none. */
  defects?: { field: string; cell: string | null; reason: string }[];
}

/**
 * One figure the WORKBOOK overrode — the deterministic spine's value replaced the model's reading
 * (ADR-0042 D4, `INGEST_SPINE_OVERRIDE`, live on staging since 2026-08-10).
 *
 * Both sides are kept deliberately: a substitution is only auditable while the reading it replaced
 * still exists.
 */
export interface SpineOverrideEntry {
  field: string;
  /** What the model read. `null` when the model had no value at all and the spine supplied one. */
  modelValue?: number | string | null;
  /** What the workbook states — and therefore the value every scored metric on the page used. */
  spineValue?: number | string;
  /** e.g. `Assumptions!C19` — the answer to "says who?". Null when the spine could not name one. */
  cell?: string | null;
  /** True when the spine FILLED a gap rather than replacing a reading. A different fact; say so. */
  filledGap?: boolean;
  /**
   * True when the field is an underwriting input the engine consumes. FALSE for the five spine-only
   * extras (`unitSF`, `hardCosts`, `softCosts`, `equity`, `netRentableSF`) — real figures the workbook
   * states that `DealInputs` has no slot for, so no metric reads them. `loanRate` is decided per run
   * (#1287): true when that run's DSCR was derived from it. Rows stored before #1287 say false for the
   * rate even where it was used.
   *
   * ⚠️ ABSENT ON RUNS PREDATING THE FLAG, and absent must render as SILENCE. Treating it as `true`
   * would re-assert the overstatement it exists to correct: this panel once told a reader every
   * overridden figure was one "the score used", when on a real 15-field override six were not.
   */
  underwritingInput?: boolean;
}

/**
 * One deal input the LIVING PROJECT RECORD pinned on this run (ADR-0069 N27): a person stated
 * `financingProgram` or `isLIHTC` on the record, and the underwrite used that over the model's reading.
 * Both sides are kept, as with the spine override.
 */
export interface RecordPin {
  field: 'financingProgram' | 'isLIHTC';
  /** What the documents read. `null` when the model stated nothing for the field. */
  modelValue: 'conventional' | 'tax-credit-subsidized' | boolean | null;
  /** What the record states — and therefore the value every scored metric used. */
  recordValue: 'conventional' | 'tax-credit-subsidized' | boolean;
  recordedByName: string | null;
  /** ISO 8601. */
  recordedAt: string;
  // No reason here: it stays on the record fact, shown to owner/admin by `GET /facts`.
}

/**
 * One tax-credit fact the documents carry — a plain-language NAME, never a figure. The server also
 * sends the signal's `key` and `strength`; the report reads only the name, so only the name is declared.
 */
export interface ProgramSignalView {
  label: string;
}

/** ADR-0069 N20 — the program-type note (mirrors the engine's `ProgramTypeNote`). */
export type ProgramTypeNoteView =
  | { kind: 'documents-tax-credit'; reading: 'market-rate'; signals: ProgramSignalView[] }
  | { kind: 'reading-tax-credit-no-signal'; reading: 'tax-credit' }
  | {
      kind: 'settled';
      settledAs: 'tax-credit' | 'market-rate';
      documents: 'tax-credit-signals' | 'no-tax-credit-signals';
      signals: ProgramSignalView[];
      pinned: { field: 'financingProgram' | 'isLIHTC'; value: string | boolean }[];
      recordedByName?: string | null;
      recordedAt: string;
    };

/** The GGPathOS-owned extraction summary an ingestion run produces (ADR-0020 Phase 1). */
export interface ExtractionSummary {
  /**
   * ⚠️ THE MODEL'S DEAL, VERBATIM — not necessarily the deal that was scored. When the spine override
   * fires, `ingestion-core` keeps this summary as the model read it and applies the workbook's values
   * to the deal the engine consumes, so the record holds both sides. Read `spineOverride` before
   * treating any field here as the figure behind the metrics.
   */
  deal?: DealInputs;
  gaps?: string[];
  fieldCount?: number;
  narrative?: string;
  model?: string;
  /** ⚠️ Also the MODEL's, for the same reason — an overridden field's real source is its spine cell. */
  provenance?: ExtractionProvenance[];
  /** The deterministic workbook read behind the figures. Absent when the package held no workbook. */
  spine?: ProformaSpineRead;
  /** Every figure the workbook moved. Absent/empty when the workbook and the model agreed. */
  spineOverride?: SpineOverrideEntry[];
  /**
   * Every deal input the record pinned over the model's reading (ADR-0069 N27). Absent when the record
   * pinned nothing. ⚠️ `deal` above stays the MODEL's — read this before treating `deal.isLIHTC` or
   * `deal.financingProgram` as what the run used.
   */
  recordPins?: RecordPin[];
  /**
   * What a reader should know about the pinned PAIR — one field pinned alone, or a pinned pair that
   * reads as contradictory (e.g. tax-credit-subsidized with isLIHTC false). Never a refusal; absent
   * when there is nothing to say.
   */
  recordPinWarnings?: string[];
  /**
   * ADR-0069 N20 — whether the documents' tax-credit facts and the program type this run used point
   * the same way. Absent when there is nothing to say, and on every run stored before it existed.
   * Annotation only: it moved no score.
   */
  programTypeNote?: ProgramTypeNoteView;
  /**
   * Documents the extraction did NOT read in full: cut at the per-document cap, or left out when the
   * package overran the total. Absent when everything fit — and on runs recorded before this existed,
   * which the report therefore says nothing about rather than calling them complete.
   */
  readLimits?: ExtractionReadLimit[];
}

/** One document the extraction read only part of, or none of (mirrors deal-analyzer's type). */
export interface ExtractionReadLimit {
  document: string;
  kind: 'truncated' | 'omitted';
  charsRead: number;
  charsTotal: number;
}

/** A source document that couldn't contribute to the underwrite — rejected at ingest, or unreadable. */
export interface ExcludedDocument {
  documentId?: string;
  filename?: string;
  reason:
    | 'no_text'
    | 'index_failed'
    | 'index_timeout'
    | 'unsupported_type'
    | 'ingest_failed'
    | 'not_found'
    /** #1421 — a person withdrew it from the project record, so the run did not read it, by rule. */
    | 'withdrawn';
}

/**
 * A source document that DID feed the underwrite but was not fully read (`ocr_partial`) — Nexus's OCR
 * hit its per-document cost ceiling part-way through. Its text is real; there was just more of it we
 * never saw. Distinct from an exclusion, and it must not be presented as one — nor as a clean read.
 */
export interface PartialDocument {
  documentId?: string;
  filename?: string;
  reason: 'ocr_partial';
}

// ── Reconciliation (module 8, ADR-0029) — cross-document figure agreement ──────────────────────────
// Mirrors @ggpathos/deal-analyzer's ReconciliationResult on the wire (the web package is workspace-
// isolated, so the shape is re-declared here). The engine owns every severity; this only renders it.

export type ReconciliationSeverity = 'material' | 'reconcile' | 'info';

/** One value a source document stated for a reconciled field. */
export interface ReconciliationCandidate {
  value: number;
  sourceDoc?: string;
  confidence: 'high' | 'medium' | 'low';
  passage?: string;
  /**
   * Value basis, where one label names different figures: `totalCost` (all-in TDC vs a hard-cost/GMP
   * figure) and `salePrice` (`gross_sale` vs `stabilized_value` / `net_sale`, #1149/#1159). Absent
   * otherwise.
   */
  basis?: string;
}

/** One field's cross-document agreement finding. */
export interface ReconciliationFinding {
  field: string;
  label: string;
  carriedValue: number | null;
  carriedSource?: string;
  candidates: ReconciliationCandidate[];
  maxDivergence: number | null;
  severity: ReconciliationSeverity;
  message: string;
}

/** The Reconciliation panel payload: the deterministic findings + the model's forensic narrative. */
export interface Reconciliation {
  findings: ReconciliationFinding[];
  /** Documents the reconciliation pass did not read in full. Absent when everything fit. */
  readLimits?: ExtractionReadLimit[];
  materialCount: number;
  reconcileCount: number;
  agreementCount: number;
  summary: string;
  narrative: string;
  model: string;
}

/**
 * WHEN a public-record value was fetched, and whether it is a stale fallback (ADR-0015 D3). Mirrors the
 * workers' `RecordAsOf`. Optional wherever it appears: runs recorded before it existed do not carry it,
 * and a missing `asOf` must render as "not stated", never as "current".
 */
export interface RecordAsOf {
  /** ISO-8601 — when the source answered, not when the run read it. */
  fetchedAt: string;
  /** TRUE when the source could not be reached and the last cached copy was served instead. */
  stale: boolean;
}

// ── Land basis (ADR-0034 D5a) — the sponsor's claimed land cost vs. the county public record ────────
// Mirrors the workers' on-result `LandBasisReport` on the wire (the web package is workspace-isolated,
// so the shape is re-declared here). The engine owns the verdict; this only renders it.

/**
 * The land-basis assessment carried on an analyzed run's `result`. Only the two decidable outcomes
 * appear: `checked` (the deed + assessor's land value corroborate a basis, and the sponsor's claim
 * diverges by `magnitudePct`) and `unverifiable` (the public record's own facts disagree, so a basis
 * cannot be established — never a sponsor accusation). `source` names the record's provenance.
 */
export type LandBasis =
  | {
      kind: 'checked';
      basis: number;
      claimed: number;
      magnitudePct: number;
      level: 'log' | 'flag' | 'stop_fix';
      source: string;
      asOf?: RecordAsOf;
    }
  | {
      kind: 'unverifiable';
      recordedSaleAmount: number;
      assessedLandValue: number;
      disagreementPct: number;
      source: string;
      asOf?: RecordAsOf;
    };

// ── What moved since the prior version (ADR-0077) — mirrors the engine's `VersionDeltas` ──────────
/** One document's comparison, as the panel reads it: its lines. Its questions are in the gate list. */
export interface VersionDeltasView {
  lines: { specBlock: string; summary: string }[];
}

// ── Property tax (Municipality, module 7) — the proforma's stabilized tax vs. the county's own rate ──
// Mirrors the workers' on-result `PropertyTaxReport`. Same workspace-isolation reason as `LandBasis`.

/**
 * The property-tax challenge carried on an analyzed run's `result`. `checked` = a credible
 * jurisdiction rate was derived from this parcel's own record and the sponsor's claim measured
 * against it (`deltaAnnual` POSITIVE = the sponsor UNDERSTATED). `unverifiable` = the county's
 * figures yield no credible rate, which is a statement about the record and never about the sponsor.
 *
 * 🔴 There is no `level`: this is REPORT ONLY and deliberately off the Variance Response Ladder until
 * it has run on real deals. A renderer must not present it as a graded finding.
 *
 * ⚠️ `impliedAnnualTax` is the tax on the FINISHED building. The parcel's own bill is never carried
 * and must never be shown as the comparison — on a ground-up deal the parcel is a vacant lot.
 */
export type PropertyTax =
  | {
      kind: 'checked';
      effectiveRate: number;
      impliedAssessedValue: number;
      impliedAnnualTax: number;
      claimedAnnualTax: number;
      deltaAnnual: number;
      magnitudePct: number;
      deltaPerDoor: number | null;
      assessmentRatio: number;
      source: string;
      asOf?: RecordAsOf;
    }
  | {
      kind: 'unverifiable';
      effectiveRate: number;
      reason: string;
      source: string;
      asOf?: RecordAsOf;
    }
  | {
      /**
       * The record resolved but our model is missing (Indiana's 2% cap), so the check did not run
       * (#1532). `reason` is the engine's reader-facing sentence. No rate and no figure.
       */
      kind: 'notModeled';
      reason: string;
      source: string;
      asOf?: RecordAsOf;
    };

/**
 * The exit cap rate held against the risk-free rate. `spreadBps` is the risk premium in basis points;
 * `band` classifies it. `inverted` means the exit cap sits at or below the risk-free rate — a negative
 * or zero premium. Rates are decimal fractions (canon R11).
 */
export interface CapRateSpread {
  exitCapRate: number;
  riskFreeRate: number;
  spreadBps: number;
  band: 'inverted' | 'thin' | 'adequate';
  /** ISO date of the benchmark observation — a rate without its date is not evidence. */
  observedOn: string;
  source: string;
}

/**
 * ADR-0069 N20 — the exit-cap reference as the SPA receives it (engine `ExitCapReference`). Every
 * figure is optional here because a free tenant receives the block without them.
 */
export interface ExitCapReferenceView {
  kind: 'reference' | 'not-applied';
  /** Always `reference, not stated` on a reference. */
  label?: string;
  reason?: string;
  market?: string;
  canonicalClass?: string;
  vintage?: string;
  classOffsetBps?: number;
  classARangeBps?: { low: number; high: number };
  capRangeBps?: { low: number; high: number };
  note?: string;
  sentence?: string;
  exitValueRange?: { atLow: number; atHigh: number } | null;
}

/**
 * Independently-sourced site + county facts. Every member is nullable: a source that had nothing, or
 * could not be reached, leaves its field null rather than being filled or hidden.
 */
export interface SiteContext {
  countyFips: string | null;
  flood: {
    zone: string;
    inSpecialFloodHazardArea: boolean;
    baseFloodElevationFt: number | null;
    firmId: string | null;
    source: string;
    asOf?: RecordAsOf;
  } | null;
  labor: {
    unemploymentRatePct: number | null;
    employment: number | null;
    /** Trailing-12-month employment change as a decimal fraction. */
    employmentTrend12mo: number | null;
    periodYear: number;
    source: string;
    asOf?: RecordAsOf;
  } | null;
  demographics: {
    population: number | null;
    medianHouseholdIncome: number | null;
    unemploymentRatePct: number | null;
    periodYear: number;
    source: string;
    asOf?: RecordAsOf;
  } | null;
  fairMarketRent: {
    areaName: string | null;
    rentByBedrooms: Record<string, number>;
    periodYear: number;
    source: string;
    asOf?: RecordAsOf;
  } | null;
  buildingPermits: {
    periodYear: number;
    unitsFivePlus: number | null;
    totalUnits: number | null;
    source: string;
    asOf?: RecordAsOf;
  } | null;
  /**
   * Qualified Opportunity Zone status under both maps, each dated (ADR-0069 N27). Absent on runs
   * recorded before N27; null when the site had no centroid or the geocoder could not answer.
   */
  opportunityZone?: {
    tract2010: string | null;
    tract2020: string | null;
    map2018: {
      designated: boolean | null;
      tractType: 'low_income_community' | 'contiguous' | null;
      expires: string;
      inEffect: boolean;
    };
    map2027: { status: 'not_certified'; effective: string };
    source: string;
    /** When this was true (the run's date). Absent only on hand-made fixtures. */
    asOf?: RecordAsOf;
  } | null;
}

/**
 * SEC EDGAR filings whose registrant name MATCHES the sponsor name asserted at submission.
 *
 * ⚠️ **A NAME MATCH, NEVER AN IDENTITY** — and the shape is built to keep the renderer honest. Two
 * companies can share a name and a sponsor may file under an affiliate, so the type carries the
 * registrant names VERBATIM as EDGAR published them and the `query` that produced them, and carries no
 * confidence score, no `isMatch`, and no resolved CIK-to-sponsor link. The panel must let a reader
 * judge the match; it must never assert it.
 *
 * `totalHits` may exceed `filings.length` — EDGAR pages — so a reader can see they are looking at a
 * page rather than the universe.
 */
export interface SponsorFilings {
  /** When EDGAR answered, and whether the list is a stale fallback. Absent on older runs. */
  asOf?: RecordAsOf;
  query: string;
  forms: string[];
  totalHits: number;
  filings: {
    accession: string;
    /** VERBATIM from EDGAR, including its `(CIK …)` suffix. Never normalized, never "corrected". */
    registrantNames: string[];
    ciks: string[];
    formType: string;
    /** ISO-8601 `YYYY-MM-DD`. */
    filedOn: string;
    incorporationStates: string[];
    businessStates: string[];
  }[];
}

/**
 * The result of an ingestion run (`POST /analyses/from-documents`). Distinct from a manual deal run,
 * whose `result` IS the bundle: an ingestion `result` WRAPS the bundle under `analysis`, plus the
 * extraction summary — and short-circuits to `needs_more_docs` (no `analysis`) when the package is
 * too thin to underwrite.
 */
export interface IngestionResult {
  outcome?: 'analyzed' | 'needs_more_docs';
  documentIds?: string[];
  runBlockingGaps?: string[];
  extraction?: ExtractionSummary;
  analysis?: PremiumDealAnalysis | null;
  /** Documents that were ingested but couldn't be read — surfaced so a partial read is never silent. */
  excludedDocuments?: ExcludedDocument[];
  /** Documents that fed the underwrite but were only partially read (`ocr_partial`). */
  partialDocuments?: PartialDocument[];
  /**
   * Cross-document figure agreement (ADR-0029). Present only when reconciliation ran (enabled + ≥2
   * readable documents). Presentational — it flags where the sources disagree; it never changed a number.
   */
  reconciliation?: Reconciliation;
  /**
   * The sponsor's claimed land cost held against the county public record (ADR-0034 D5a). Present only
   * when the land-basis check ran (enabled + a resolvable site + a stated land cost). Presentational —
   * the persisted variance is the durable record; this renders the finding.
   */
  landBasis?: LandBasis;
  /**
   * The proforma's stabilized property tax held against the jurisdiction's own effective rate
   * (Municipality, module 7). Present only when the challenge reached a decidable outcome — it needs a
   * claimed tax, the county's tax + assessed pair, a stabilized value, and a derivable assessment
   * ratio, and declines by name rather than assuming any of them.
   */
  propertyTax?: PropertyTax;
  /**
   * External checks that were asked to run and THREW. Present only when something broke.
   *
   * Absence is unambiguous BECAUSE the bundle's `externalChecks` already says what was wired: wired, not
   * in this list, no panel ⇒ the check looked and found nothing worth flagging.
   */
  externalCheckFailures?: readonly string[];
  /** Wired checks the run held no key for — it never looked, which is not the same as finding nothing. */
  externalChecksUnkeyed?: readonly string[];
  /**
   * #1540 — wired checks that ran and declined on a missing input (a thin county record, no NOI or exit
   * cap). Ours only; the published insight does not carry it.
   */
  externalChecksDeclined?: readonly { check: string; reason: string }[];
  /**
   * The exit cap rate held against the contemporaneous risk-free rate. Present only when the check ran
   * (enabled + a stated exit cap + a reachable benchmark). Presentational — it reports a magnitude and
   * a band, never a verdict, and it never moved a number.
   */
  capRateSpread?: CapRateSpread;
  /**
   * Independently-sourced facts about the site and its county, from government records the sponsor
   * does not control. Present only when the gather ran. Every field is null when its source had
   * nothing — a gap is stated, never filled. Never fed to the model; never scored.
   */
  siteContext?: SiteContext;
  /**
   * Public securities filings matching the sponsor name asserted at submission. Present only when the
   * lookup ran AND a name was supplied AND EDGAR held something. A NAME MATCH, never an identity —
   * never fed to the model, never scored.
   */
  sponsorFilings?: SponsorFilings;
  /**
   * Set on AUTONOMOUS (system-initiated) runs: the underwrite is computed but held for human review —
   * not yet published to the client. Absent on human-initiated runs (released by definition).
   */
  reviewState?: 'pending_review' | 'released';
  /**
   * The construction-budget read's own account of itself. Only `readLimits` is typed here — the report
   * reads nothing else off it yet, and typing unread fields is what the orphan-field sweep refuses.
   */
  constructionBudget?: {
    readLimits?: ExtractionReadLimit[];
    /** Lines stated without a division breakdown. Scope coverage names an un-itemized one (A11). */
    omissions?: { entries?: BudgetOmissionView[] };
  };
}

/** A line the estimate states without a division breakdown, as the construction-budget read recorded it. */
export interface BudgetOmissionView {
  label: string;
  dollars: number;
  /** `un-itemized` can hold a modelled division; `outside-benchmarked-divisions` (11, 12) cannot. */
  reason?: string;
}

/** A run's `result` is either a manual-deal bundle or an ingestion wrapper. */
export type AnalysisResult = PremiumDealAnalysis | IngestionResult;

/** Discriminate the ingestion wrapper from a bare bundle (the wrapper carries `outcome`). */
export function isIngestionResult(r: AnalysisResult | null | undefined): r is IngestionResult {
  return r != null && 'outcome' in r;
}

/**
 * A street address as ASSERTED — typed into a form, or read out of a document package. Mirrors
 * `@ggpathos/db`'s `SiteAddress`. Possibly incomplete and possibly unresolvable: it is what someone
 * told us, never what a county confirmed.
 */
export interface SiteAddress {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}

/** The deal's physical identity (ADR-0034 D3). `apn`/`fips` stay null until a parcel resolve fills them. */
export interface SiteIdentity {
  address?: SiteAddress | null;
  apn?: string | null;
  fips?: string | null;
  resolvedAt?: string | null;
}

/** The project a run was filed under — `code` is the durable folder key, `name` the label. */
export interface ProjectIdentity {
  code?: string | null;
  name?: string | null;
}

export interface AnalysisRun {
  id: string;
  status: AnalysisStatus;
  result: AnalysisResult | null;
  error: string | null;
  /**
   * The failure's taxonomy code (C7), null unless the run failed. Optional: a server from before the
   * field omits it. Read for ONE code only — see `failedRunMessage` in AnalysisView.
   */
  errorCode?: string | null;
  model: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  /**
   * How far along the run is (#1400) — present on every response from a server that knows the field,
   * `null` when the run has reported no stage (older runs, deal runs, not reached yet) or has ended.
   * Optional because a response from before the field existed omits it; both read as "no stage".
   */
  progress?: AnalysisProgress | null;
  /**
   * ── Deal identity ────────────────────────────────────────────────────────
   * WHICH deal this run underwrote. Every one is optional and independently absent, because each
   * arrives by its own route: `site` from the manual form or from extraction, `sponsorName` only if
   * someone typed one, `project` only if the run was filed under one. A field that is absent is
   * omitted from the report rather than rendered empty — the report states what is known about the
   * deal and stays silent about the rest.
   */
  site?: SiteIdentity | null;
  sponsorName?: string | null;
  project?: ProjectIdentity | null;
  /**
   * ── Contact consent, as answered at submission (Ground Truth §2.4, #569) ─
   * `null` is the THIRD state and not a synonym for `'declined'`: an unticked box is the client's
   * answer, an absent box is our omission. A run started by the system or by MCP was never asked,
   * and reading that as a refusal would attribute a decision to someone who was never offered one.
   */
  contactConsent?: ContactConsentState | null;
  /** When the answer was given. Present exactly when `contactConsent` is — the DB CHECK guarantees it. */
  contactConsentAt?: string | null;
  /**
   * The id of a NEWER succeeded underwrite for the same project — R8 currency, on the report.
   *
   * ⚠️ ABSENT MEANS TWO DIFFERENT THINGS and the reader must not be told the wrong one: an unbound
   * run (no `project`) can have no successor, so the question does not apply; a bound run with no
   * successor IS current. The renderer decides from `project`, never from this field alone —
   * claiming "this is the latest" about a run that was never checked is the same false-clean the
   * project badge already guards against.
   */
  supersededBy?: string;
  /**
   * The immediately-preceding succeeded underwrite of the same project — the baseline for the
   * report's "what changed" block (R1).
   *
   * ⚠️ ABSENT MEANS TWO DIFFERENT THINGS, exactly as `supersededBy` does: an unbound run has no
   * project and therefore no previous version, while a bound run without one is the FIRST underwrite
   * of its project. The renderer decides from `project`.
   */
  previousUnderwrite?: PreviousUnderwrite;
}

/**
 * The previous version's headline figures — four numbers and a verdict, and nothing else.
 *
 * ⚠️ EVERY FIELD IS INDEPENDENTLY NULLABLE and a null is NOT "unchanged". A run that never stated a
 * DSCR and a run whose DSCR held steady are different facts about the deal; collapsing them would
 * report a steadiness nobody measured.
 *
 * The set is deliberately small and every member is free-tier safe (the verdict and score are what
 * `redactScore` keeps; DSCR and yield-on-cost ARE `FREE_TIER_METRICS`), so this travels identically
 * to every tenant. Adding a paid figure here would be an entitlement change, not a UI change.
 */
export interface PreviousUnderwrite {
  analysisId: string;
  completedAt: string | null;
  recommendation: string | null;
  overallScore: number | null;
  dscr: number | null;
  yieldOnCostPct: number | null;
  /** How many documents fed THAT run. Null on a manual run, which ingested none. */
  documentCount: number | null;
}

/** The client's answer to the contact-consent box. Absent (`null`) means never asked. */
export type ContactConsentState = 'granted' | 'declined';

/**
 * One row of the tenant's run history — enough to recognise a deal and decide whether to open it,
 * and deliberately no more. The api extracts these scalars with JSON paths rather than shipping the
 * bundle, so a list of 50 runs costs 50 short rows instead of 50 IC memos.
 */
export interface AnalysisSummary {
  id: string;
  status: AnalysisStatus;
  createdAt: string;
  completedAt: string | null;
  site?: SiteIdentity | null;
  sponsorName?: string | null;
  project?: ProjectIdentity | null;
  score?: number | null;
  recommendation?: Verdict | null;
  /**
   * ── Engagement: has the client actually opened this? (operator-only) ─────
   *
   * Present only when the caller can write — a client is not shown a counter of their own opens,
   * because Brad's tenant-level ruling deliberately deferred the question of whether they are told at
   * all. Absent therefore means "not for you", never "nobody has read it".
   *
   * ⚠️ `0` IS A REAL ANSWER and the most actionable one there is: nobody has opened this report. It is
   * a number, never an omission — an absent key on zero would make the row that most needs attention
   * look identical to a row from before the feature existed.
   *
   * ⚠️ Counts CLIENT opens only. GGP staff must be members of a client's tenant to produce that
   * client's reports, so their reads sit in the same table; counting them would report our own
   * activity back to us as the client's engagement.
   */
  clientViews?: number;
  /** When the client last opened it. `null` means never — distinct from the field being absent. */
  lastClientViewAt?: string | null;
  /**
   * A NEWER succeeded underwrite of the same project exists — R8 currency, on the list (X1).
   *
   * ⚠️ ABSENT MEANS THE QUESTION DOES NOT APPLY, never "current": an unbound run belongs to no chain
   * and an unfinished run is not a version of anything yet. `false` is the real "this is the latest".
   * The server sends the key only when it means something — the same discipline as `supersededBy`.
   *
   * ⚠️ Decided across the WHOLE tenant, not the page — and the honest reason is narrower than the
   * one that used to sit here ("a paged list's top row is not necessarily the newest run"), which is
   * FALSE under `created_at DESC` ordering. The real reasons: the list returns runs of every status
   * while supersession counts only succeeded ones, and the page's ordering/limit/filter are
   * presentation, so deriving a correctness claim from them means currency starts lying the day
   * someone adds sort-by-score. See the route for the full note.
   */
  superseded?: boolean;
  /**
   * How many documents entered this project's record AFTER this underwrite began — the second half
   * of R8 currency, and the one a list row could not answer until now. A current report can be
   * overtaken by the package it was produced from, and the list is where someone chooses what to
   * open.
   *
   * ⚠️ ABSENT AND `0` ARE DIFFERENT SENTENCES. Absent = not asked: the run is superseded (it says so
   * already), unfinished, or bound to no project record, so there is no document set to have moved.
   * `0` = asked, and nothing moved. Only a value `> 0` renders — the same asymmetry as `superseded`,
   * whose `false` is also sent and also silent.
   *
   * ⚠️ Counted from the run's `created_at` (its START), which over-reports rather than under-reports
   * a file that landed mid-run. Deliberate: a needless re-run is recoverable, a stale number that
   * looks current is not.
   *
   * ⚠️ DOCUMENTS NEW TO THE RECORD; a newer version of one already on it is `documentsRevisedSince`.
   */
  documentsAddedSince?: number;
  /** Documents this underwrite could have read that have a newer version since. Same absent rule. */
  documentsRevisedSince?: number;
}

/**
 * Tell the server this report was opened. Fire-and-forget, by design.
 *
 * ⚠️ NEVER awaited by the render path and NEVER surfaced on failure. This is an engagement signal, not
 * part of the report: a reader whose beacon 404s or is rate-limited must still see their underwrite.
 * The one thing worse than losing a usage row is breaking the page that produced it.
 *
 * Takes no body — `viewer_kind` is derived server-side from the caller's role, so the browser cannot
 * choose which bucket it lands in.
 */
export async function recordReportView(id: string): Promise<void> {
  try {
    await request(`/analyses/${encodeURIComponent(id)}/viewed`, { method: 'POST' });
  } catch {
    // Deliberately silent — see above.
  }
}

/**
 * The tenant's runs, newest first. Returns `[]` on any failure rather than throwing: history is a
 * convenience surface, and a reader who cannot reach it should see an empty state, never a broken page.
 */
export async function getAnalyses(
  opts: { limit?: number; project?: string } = {},
): Promise<AnalysisSummary[]> {
  try {
    const params = new URLSearchParams({ limit: String(opts.limit ?? 50) });
    if (opts.project) params.set('project', opts.project);
    const res = await request(`/analyses?${params.toString()}`);
    if (!res.ok) return [];
    const body = (await res.json()) as { analyses?: AnalysisSummary[] };
    return body.analyses ?? [];
  } catch {
    return [];
  }
}

/** Raised when the async runner is unavailable (503) — e.g. off-hours worker scale-to-zero. */
export class RunnerUnavailableError extends Error {}

/**
 * Enqueue a premium analysis. Returns the new run's id. Throws `RunnerUnavailableError` on 503
 * (the worker isn't configured/awake), and a generic Error on other non-2xx.
 */
export async function createAnalysis(
  deal: DealInputs,
  opts?: {
    reviewerContext?: string | null;
    sponsorName?: string | null;
    sponsorContext?: string | null;
    /**
     * Ground Truth §2.4 contact consent. ⚠️ PRESENCE IS MEANINGFUL — `true` granted, `false`
     * declined, OMITTED means the surface never asked. Never default it.
     */
    contactConsent?: boolean;
  },
): Promise<string> {
  const res = await request('/analyses', {
    method: 'POST',
    body: JSON.stringify({
      deal,
      reviewerContext: opts?.reviewerContext ?? null,
      // Spread, never `?? null`: an absent field is "never asked", and collapsing it into `false`
      // would record a client refusal this surface never collected (Ground Truth §2.4).
      ...(opts?.contactConsent === undefined ? {} : { contactConsent: opts.contactConsent }),
      // Sent BESIDE the deal, never inside it — the api stores it on its own column and the engine
      // never sees it. Null when the submitter left it blank.
      sponsorName: opts?.sponsorName ?? null,
      // Also beside the deal, and a DIFFERENT field from sponsorName: the name is a lookup key, this
      // is the evidence the Sponsor module judges. Null when blank — never defaulted from anything.
      sponsorContext: opts?.sponsorContext ?? null,
    }),
  });
  if (res.status === 503) {
    throw new RunnerUnavailableError('The analysis runner is offline. Please try again shortly.');
  }
  if (!res.ok) {
    throw new Error('The analysis didn’t start. Give it another try in a moment.');
  }
  const body = (await res.json()) as { id: string };
  return body.id;
}

/** One uploaded document, base64-encoded, for the ingestion loop. */
export interface UploadDocument {
  filename: string;
  contentType: string;
  contentBase64: string;
}

/**
 * Enqueue an ingestion run from a document package (ADR-0020 Phase 1): the worker ingests the docs
 * into Nexus, extracts the deal inputs, and runs the analyzer (or flags `needs_more_docs`). Returns
 * the run id — poll it with {@link getAnalysis}, same as a manual run. 503 → runner offline; 413 →
 * the upload exceeded the server body limit.
 */
export async function createAnalysisFromDocuments(
  documents: UploadDocument[],
  opts?: {
    reviewerContext?: string | null;
    project?: { code: string; name?: string | null } | null;
    /** The asserted sponsor name — the SEC EDGAR search key. This is the path that looks it up. */
    sponsorName?: string | null;
    /** The asserted sponsor EVIDENCE. Extraction never recovers this from the package. */
    sponsorContext?: string | null;
    /**
     * Ground Truth §2.4 contact consent. ⚠️ PRESENCE IS MEANINGFUL — `true` granted, `false`
     * declined, OMITTED means the surface never asked. Never default it.
     */
    contactConsent?: boolean;
  },
): Promise<string> {
  const res = await request('/analyses/from-documents', {
    method: 'POST',
    body: JSON.stringify({
      documents,
      reviewerContext: opts?.reviewerContext ?? null,
      // Spread, never `?? null`: an absent field is "never asked", and collapsing it into `false`
      // would record a client refusal this surface never collected (Ground Truth §2.4).
      ...(opts?.contactConsent === undefined ? {} : { contactConsent: opts.contactConsent }),
      project: opts?.project ?? null,
      sponsorName: opts?.sponsorName ?? null,
      sponsorContext: opts?.sponsorContext ?? null,
    }),
  });
  if (res.status === 503) {
    throw new RunnerUnavailableError('The analysis runner is offline. Please try again shortly.');
  }
  if (res.status === 413) {
    throw new Error('Those documents are too large to upload. Try fewer or smaller files.');
  }
  if (!res.ok) {
    throw new Error('The analysis didn’t start. Give it another try in a moment.');
  }
  const body = (await res.json()) as { id: string };
  return body.id;
}

/** A tenant project for the upload form's picker (GET /projects). */
export interface ProjectSummary {
  /**
   * The record's stable id. The only handle a PURSUIT has — see `code`.
   *
   * Optional here, and only because rows fetched before this field was read would otherwise be
   * retroactively invalid. The server has always sent it.
   */
  id?: string;
  /**
   * ⚠️ NULL FOR A PURSUIT, and this type used to say `string`.
   *
   * The server has always declared `code: string | null` (canon R-e: "a pursuit carries no project
   * number… numbers are assigned AT promotion — never before"). This mirror said `string`, so the list
   * keyed on `p.code` and navigated to `/projects/${p.code}` — which for a pursuit is `/projects/null`.
   *
   * It had never fired because every record on staging happens to carry a number. It is fixed in the
   * same change that adds the button which CREATES pursuits, because that is the change that turns a
   * latent type lie into a broken row.
   */
  code: string | null;
  name: string | null;
  /** Which class of record — the list must be able to say "pursuit" rather than show a blank code. */
  recordClass?: RecordClass;
  /**
   * The permanent job number (`2026-0001-Dev`), minted at Established.
   *
   * ⚠️ THE ONLY IDENTIFIER A PURSUIT HAS — `code` stays null until promotion. Without it a list of
   * pursuits is a list of names, which is what thirteen simultaneously-imported projects looked like.
   */
  jobNumber?: string | null;
}

/**
 * The caller tenant's known projects, for the upload picker. Returns [] on any failure (401 / offline)
 * — the picker degrades to "type a new project", never blocks the upload form.
 */
export async function getProjects(q?: string): Promise<ProjectSummary[]> {
  try {
    // Bare when there is no term — the upload picker calls this for the full list, and a `?q=`
    // hanging off every request would make that dependent on the server's empty-string handling.
    const path = q && q.trim() !== '' ? `/projects?q=${encodeURIComponent(q.trim())}` : '/projects';
    const res = await request(path);
    if (!res.ok) return [];
    const body = (await res.json()) as { projects?: ProjectSummary[] };
    return Array.isArray(body.projects) ? body.projects : [];
  } catch {
    return [];
  }
}

/** One document hit from the find verb — the CURRENT version, with the project that holds it. */
export interface DocumentHit {
  documentId: string;
  filename: string;
  documentType?: string | null;
  versionNumber: number;
  submittedByName?: string | null;
  submittedAt: string;
  /**
   * How to ADDRESS the project — its code, or its uuid when it has none.
   *
   * ⚠️ NOT the code. `project_code` is null for every pursuit (canon R5/R-e), so a URL built from
   * the code alone reads `/projects/null/...` and every document on a pursuit becomes unopenable —
   * a whole record class silently excluded from R8's "any accessible document".
   */
  projectRef: string;
  /** The code when the record has one — for DISPLAY only, and null on a pursuit. */
  projectCode?: string | null;
  projectName?: string | null;
  /** Set when the current stamp has been withdrawn; the row must say so rather than hide it. */
  withdrawnAt?: string | null;
  withdrawnByName?: string | null;
}

/**
 * What a find returned — or that it could not be asked.
 *
 * ⚠️ `'error'` IS A DISTINCT STATE, and it exists because collapsing it into `[]` makes the panel
 * state a fact it never established: "no document matches this search" is a claim about the
 * tenant's records, and a 500 or an offline browser is not evidence for it. Same rule the panel
 * itself follows for an empty result, defeated one layer down if this returns `[]` on failure.
 */
export type DocumentFindResult = { documents: DocumentHit[]; truncated: boolean } | 'error';

/**
 * Find a document by name or type across the tenant — canon R8's primary surface.
 *
 * ⚠️ Returns the CURRENT version of each document and nothing else. The server decides what
 * "current" means (the newest stamp, shown withdrawn when the document has an unrestored withdrawal on
 * its record — migration 0066); this client must never re-rank or
 * re-filter, because a second opinion about currency held on the client is exactly how a superseded
 * drawing reaches a reader.
 *
 * Returns [] on any failure, like `getProjects` — a find box that throws is worse than one that
 * finds nothing, and the project search beside it stays usable.
 */
export async function findDocuments(q: string): Promise<DocumentFindResult> {
  const term = q.trim();
  // The server enforces a two-character floor; asking below it would spend a round trip to be told.
  if (term.length < 2) return { documents: [], truncated: false };
  try {
    const res = await request(`/documents?q=${encodeURIComponent(term)}`);
    if (!res.ok) return 'error';
    const body = (await res.json()) as { documents?: DocumentHit[]; truncated?: boolean };
    if (!Array.isArray(body.documents)) return 'error';
    return { documents: body.documents, truncated: body.truncated === true };
  } catch {
    return 'error';
  }
}

/** The three record classes a person can open (canon R-a/R-e; `@ggpathos/lifecycle`). */
export type RecordClass = 'analyzed_deal' | 'pursuit' | 'ggp_project';

export interface CreateProjectInput {
  recordClass: RecordClass;
  projectName: string;
  /** ONLY for `ggp_project` — migrating a record that already carries its number. */
  projectCode?: string | null;
  sponsorName?: string | null;
  sponsorContext?: string | null;
  /**
   * The engagement category, which decides the TRAILING TOKEN of the permanent job number
   * (`2026-0001-Dev`). Chad Garrett's vocabulary; omitted or unrecognised takes his own `Proj`
   * fallback.
   *
   * ⚠️ The number is minted at Established and never reissued, so this choice is permanent from the
   * moment the record exists. That is why the form shows the token it will mint rather than leaving
   * it to be discovered afterwards.
   */
  engagementCategory?: string | null;
  /**
   * Where this record came from, when it came from somewhere — a GGP Project Tracking Log row number
   * (`26.100.01`). Null for a record typed in from scratch, which is most of them.
   *
   * ⚠️ NOT a project number and not a job number. It exists so a second import of the same log row is
   * refused rather than duplicated (migration 0044).
   */
  sourceRef?: string | null;
  /**
   * The Lead Owner, by name. The Variance Response Ladder stamps it onto every stop it opens, so a
   * record opened without one opens ownerless stops. Name only: linking it to a member stays on the
   * Lead Owner route, which needs `membership:read`.
   */
  leadOwnerName?: string | null;
}

/** One engagement category as the server offers it — Chad's string, and the token it mints. */
export interface EngagementCategoryOption {
  value: string;
  typeCode: string;
}

/**
 * The engagement categories, FETCHED rather than hard-coded here.
 *
 * ⚠️ This list is Chad's and is his to extend (`@ggpathos/lifecycle`'s `job-number.ts`). This package
 * has no workspace dependencies, so the only alternative was a second copy of his vocabulary living
 * in the SPA — which drifts the day he adds a category, and would let this form offer one the minter
 * has never heard of.
 *
 * Degrades to an empty list on any failure, exactly as `getProjects` does: the picker then offers only
 * "Something else", the server applies its own fallback, and intake still works. A category selector
 * that could block record creation would be a worse failure than an unlabelled record.
 */
export async function getEngagementCategories(): Promise<{
  categories: EngagementCategoryOption[];
  fallbackTypeCode: string;
}> {
  try {
    const res = await request('/projects/engagement-categories');
    if (!res.ok) return { categories: [], fallbackTypeCode: 'Proj' };
    const body = (await res.json()) as {
      categories?: EngagementCategoryOption[];
      fallbackTypeCode?: string;
    };
    return {
      categories: Array.isArray(body.categories) ? body.categories : [],
      fallbackTypeCode: body.fallbackTypeCode ?? 'Proj',
    };
  } catch {
    return { categories: [], fallbackTypeCode: 'Proj' };
  }
}

/**
 * The outcome of intake. A REFUSAL IS NOT AN ERROR — canon R7: "anywhere the gate surfaces a
 * conclusion that binds a person… it presents as a recommendation carrying a Human Decision Point,
 * never an unappealable verdict." `planIntake` refuses rather than throws, and every refusal carries
 * prose written to be read by the person who tripped it ("a pursuit carries no project number — 25-014
 * would be assigned before the IC decides"). Collapsing that into "Bad Request" throws away the only
 * part of the response that tells someone what to do next.
 */
export type CreateProjectResult =
  | { status: 'created'; project: { id: string; code: string | null; name: string | null } }
  | { status: 'refused'; reason: string; refusal?: string }
  | { status: 'forbidden'; reason: string }
  | { status: 'conflict'; reason: string }
  | { status: 'error'; reason: string };

/**
 * ONE PROPOSAL from the tracking log, as the preview route returns it. Mirrors
 * `PortfolioProposal` in `@ggpathos/lifecycle` plus the one thing only the server can answer.
 */
export interface PortfolioProposal {
  /** The log's own number (`26.100.01`) — the key a second import would collide on. */
  sourceRef: string;
  recordClass: RecordClass;
  projectName: string;
  /*
    ⚠️ NO `projectCode`. The planner never proposes one — a project number is minted at promotion by a
    person (R-e) — so the route does not send it and this type does not claim it.
  */
  sponsorName: string | null;
  city: string | null;
  state: string | null;
  stated: {
    projectType: string | null;
    /** The log's own status word ("Pending", "Active") — read by the log-figures assertion. */
    projectStatus: string | null;
    designStatus: string | null;
    units: number | null;
    buildingSf: number | null;
    developerBudget: number | null;
    fee: number | null;
    estimate: number | null;
    dateUpdated: string | null;
  };
  warnings: string[];
  /**
   * The record this log row ALREADY became, or null. Computed server-side because this bundle cannot
   * see the tenant's records — and without it the page would offer a row that 409s after confirm
   * rather than showing it as already done before.
   */
  existing: { id: string; code: string | null; name: string | null } | null;
}

/** A row the log states that the planner will not propose, and why. */
export interface PortfolioSkip {
  rowIndex: number;
  sourceRef: string | null;
  reason: string;
}

export interface PortfolioPreview {
  proposals: PortfolioProposal[];
  skipped: PortfolioSkip[];
  totals: {
    proposed: number;
    units: number;
    buildingSf: number;
    developerBudget: number;
    fee: number;
    estimate: number;
    /** How many proposals contributed to `estimate` — a total that hides its denominator misleads. */
    withEstimate: number;
  };
}

export type PortfolioPreviewResult =
  | { status: 'ok'; preview: PortfolioPreview }
  | { status: 'refused'; reason: string; rowIndex?: number }
  | { status: 'forbidden'; reason: string }
  | { status: 'error'; reason: string };

/**
 * Ask the server what it WOULD create from these tracking-log rows. Writes nothing.
 *
 * ⚠️ Sends the ROWS, never a plan computed here. The mapping (which status becomes a numbered project,
 * which row is skipped) runs server-side where it is tested — a plan assembled in this bundle would
 * make `classFor` a client-side decision.
 */
export async function previewPortfolio(rows: unknown[]): Promise<PortfolioPreviewResult> {
  try {
    const res = await request('/portfolio/preview', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ rows }),
    });
    if (res.ok) return { status: 'ok', preview: (await res.json()) as PortfolioPreview };
    const body = (await res.json().catch(() => ({}))) as { error?: string; rowIndex?: number };
    if (res.status === 403) {
      return { status: 'forbidden', reason: body.error ?? 'You cannot import a portfolio.' };
    }
    if (res.status === 400) {
      return {
        status: 'refused',
        reason: body.error ?? 'That log could not be read.',
        ...(typeof body.rowIndex === 'number' ? { rowIndex: body.rowIndex } : {}),
      };
    }
    return { status: 'error', reason: `The preview failed (${String(res.status)}).` };
  } catch {
    return { status: 'error', reason: 'The preview could not be reached.' };
  }
}

/**
 * The log's stated figures for one row, asserted onto the record that log row already became.
 *
 * 🔴 THE ANSWER TO A ROW THAT IS "already imported". `POST /projects` refuses a second import of the
 * same source reference, correctly — but the sheet is hand-maintained and its numbers move, and until
 * this existed an updated row was a dead end. The figures land as `record_facts` (Chad's 2026-08-13
 * ruling), which the record already renders.
 */
export type LogFiguresResult =
  | { status: 'ok'; asserted: number; assertedKeys: string[]; unchanged: number }
  | { status: 'refused'; reason: string }
  | { status: 'forbidden'; reason: string }
  | { status: 'error'; reason: string };

export interface LogFiguresInput {
  /** The record — its number, or its id for a pursuit, which carries none (canon R5/R-e). */
  ref: string;
  sourceRef: string;
  dateUpdated: string | null;
  stated: {
    unitQty: number | null;
    buildingSf: number | null;
    developerBudget: number | null;
    fee: number | null;
    estimate: number | null;
    projectStatus: string | null;
    designStatus: string | null;
  };
}

export async function assertLogFigures(input: LogFiguresInput): Promise<LogFiguresResult> {
  try {
    const res = await request(`/projects/${encodeURIComponent(input.ref)}/log-figures`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        sourceRef: input.sourceRef,
        dateUpdated: input.dateUpdated,
        stated: input.stated,
      }),
    });
    if (res.ok) {
      const body = (await res.json()) as {
        asserted?: number;
        assertedKeys?: string[];
        unchanged?: number;
      };
      return {
        status: 'ok',
        asserted: body.asserted ?? 0,
        assertedKeys: body.assertedKeys ?? [],
        unchanged: body.unchanged ?? 0,
      };
    }
    // The server's own words wherever it gave any — a 409 here is a real, explainable answer
    // ("this record carries no source reference"), not a generic failure.
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 403) {
      return { status: 'forbidden', reason: body.error ?? 'You cannot assert on this record.' };
    }
    if (res.status === 400 || res.status === 404 || res.status === 409) {
      return { status: 'refused', reason: body.error ?? 'Those figures were not recorded.' };
    }
    return { status: 'error', reason: `That did not go through (${String(res.status)}).` };
  } catch {
    return { status: 'error', reason: 'We couldn’t reach the server. Check your connection.' };
  }
}

export async function createProject(input: CreateProjectInput): Promise<CreateProjectResult> {
  try {
    const res = await request('/projects', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.ok) {
      const body = (await res.json()) as {
        project?: { id: string; code: string | null; name: string | null };
      };
      return body.project
        ? { status: 'created', project: body.project }
        : { status: 'error', reason: 'The record was created but the response was unreadable.' };
    }
    // The server's own words wherever it gave any — see the R7 note above.
    const body = (await res.json().catch(() => ({}))) as { error?: string; refusal?: string };
    if (res.status === 400) {
      return {
        status: 'refused',
        reason: body.error ?? 'That record cannot be opened as described.',
        ...(body.refusal ? { refusal: body.refusal } : {}),
      };
    }
    if (res.status === 403) {
      return { status: 'forbidden', reason: body.error ?? 'You cannot create a record.' };
    }
    if (res.status === 409) {
      return {
        status: 'conflict',
        reason: body.error ?? 'A record with that project number already exists.',
      };
    }
    return { status: 'error', reason: 'We couldn’t open the record just now. Try again shortly.' };
  } catch {
    return { status: 'error', reason: 'We couldn’t reach the server. Check your connection.' };
  }
}

/**
 * `GET /analyses/:id` answered 404 — no such run in the caller's tenant (RLS makes another tenant's
 * run "not found"). A DIFFERENT fact from a failed request: retrying will not change it. Same message as
 * the generic failure, so a caller that only reads `.message` (the report page) reads exactly what it
 * read before; a caller that cares (`ReunderwriteRun`) tells the two apart by type.
 */
export class AnalysisNotFoundError extends Error {}

/** Poll a run. Returns null on 401 (session gone) so the caller can route to sign-in. */
export async function getAnalysis(id: string): Promise<AnalysisRun | null> {
  const res = await request(`/analyses/${id}`);
  if (res.status === 401) return null;
  if (res.status === 404) {
    throw new AnalysisNotFoundError(
      'We couldn’t load this analysis just now. Refresh to try again.',
    );
  }
  if (!res.ok) throw new Error('We couldn’t load this analysis just now. Refresh to try again.');
  return (await res.json()) as AnalysisRun;
}

/**
 * Cancel a run (POST /analyses/:id/cancel). Returns the run's resulting status — `canceled` if it was
 * still live, or its actual terminal status if it had already finished (idempotent). Throws on 404
 * (unknown run) or other non-2xx. Cancellation is cooperative server-side: a run mid-model-call
 * finishes that call, but the result is discarded and the row stays `canceled`.
 */
export async function cancelAnalysis(id: string): Promise<AnalysisStatus> {
  const res = await request(`/analyses/${id}/cancel`, { method: 'POST' });
  if (!res.ok) {
    throw new Error('We couldn’t stop the run just now. Give it another try in a moment.');
  }
  const body = (await res.json()) as { status: AnalysisStatus };
  return body.status;
}

// ── The living project record (LPR) — record-state, CRP-1, Decision timeline (ADR-0028 / M3 spine) ──
// The GGPathOS-owned governance surface: a project advances through a record-state machine, and every
// state change / capital-release approval is an immutable Decision Record. These view-types mirror the
// api routes (packages/api/src/routes/projects.ts + crp.ts) and stay PERMISSIVE where the wire is (the
// web package is workspace-isolated — no @ggpathos/* imports — so the shared enums are re-declared here).

/** The canonical record-state ladder (Set B). Kept in lockstep with @ggpathos/engine RECORD_STATES. */
export type RecordState =
  | 'established'
  | 'underwritten'
  | 'vetted'
  | 'entitled'
  | 'designed'
  | 'preconstruction'
  | 'financed'
  | 'built'
  | 'opened'
  | 'stabilized'
  | 'operating'
  | 'realized';

export const RECORD_STATES: readonly RecordState[] = [
  'established',
  'underwritten',
  'vetted',
  'entitled',
  'designed',
  'preconstruction',
  'financed',
  'built',
  'opened',
  'stabilized',
  'operating',
  'realized',
];

/** A compact summary of a project's latest underwrite — engine-owned numbers, read straight off the run. */
export interface UnderwriteSummary {
  status?: 'queued' | 'running' | 'awaiting_index' | 'succeeded' | 'failed' | 'canceled';
  overallScore?: number | null;
  verdict?: Verdict | null;
  structural?: boolean;
}

/**
 * A deterministic gate check's QUESTION about the documents a record was underwritten from — C1
 * identity, C3 staleness, D6 self-consistency (gate tranche B).
 *
 * ⚠️ IT DECIDES NOTHING. Canon's shape is "prepared for your review, not decided": the documents are
 * on the record, the underwrite ran, and the score is unchanged by any finding here. Render it as a
 * question for a person, never as a failure.
 */
export interface GateFinding {
  /** Which check spoke — `identity` | `self-consistency` | `staleness`. */
  check: string;
  /*
   * ⚠️ NO `severity` FIELD, and its absence is the point. The record and the Nexus wire both carry
   * one, and it is `'question'` on every finding by contract — "a severity ladder here would invite a
   * renderer to style one as a failure". A constant tells a renderer nothing, so the api does not
   * serve it and this card styles from `check` alone.
   */
  /** One line stating what was found, in plain language. */
  summary: string;
  /** What a person can do about it. Never a repair instruction — the engine chooses no values. */
  resolution: string;
  /** The gate spec block, for traceability against Chad's prototype. */
  specBlock: string;
  /** Which question this is, across runs (ADR-0069 N12). Served to a staff reader only. */
  findingKey?: string;
  /** A named person's answer, once given — on this run or an earlier one asking the same thing. */
  answer?: GateAnswer;
  /** The answers this question's kind offers, while it is open. Chad's ruled words, from the server. */
  responses?: { id: string; sentence: string }[];
}

/** An answer to a gate question, as the record holds it (ADR-0069 N12). */
export interface GateAnswer {
  /** The answer's sentence as the person read it when they chose it. */
  sentence: string;
  reasoning: string;
  answeredByName: string;
  answeredAt: string;
}

/**
 * Answer a gate question (operators, server-enforced). The answerer's NAME is not sent: the server
 * takes it from the session. The question is named by its key and read back from the record on the
 * server, so only a question the gate asked can be answered.
 */
export async function answerGateQuestion(
  code: string,
  input: { findingKey: string; response: string; reasoning: string },
): Promise<void> {
  const res = await request(`/projects/${encodeURIComponent(code)}/gate-questions/answers`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(
      await disputeRefusal(res, 'We couldn’t record that answer just now. Give it another try.'),
    );
  }
}

/** The living project record header (GET /projects/:code). */
export interface ProjectDetail {
  code: string;
  name: string | null;
  recordState: RecordState;
  /**
   * ⚠️ RAW: the length of the record's `document_ids`, withdrawn documents included. Not what a reader
   * means by "documents" — show `judgedDocumentCount`.
   */
  documentCount: number;
  /**
   * How many documents an underwrite of this record judges: `document_ids` less withdrawals, by the
   * same rule the re-underwrite door and the worker use. The header and the re-underwrite confirm
   * show THIS number, never a client-side estimate.
   *
   * ⚠️ OPTIONAL ON PURPOSE: a web build can briefly run ahead of the api it talks to. Absent is NOT
   * zero — a reader shown "0 judged" would be told something false — so an absent count says nothing.
   */
  judgedDocumentCount?: number;
  latestUnderwrite: UnderwriteSummary | null;
  /** The run behind `latestUnderwrite` — the route to the report (HUB-3). Null when never underwritten. */
  latestAnalysisId?: string | null;
  /**
   * The previous underwrite of this record, by the SAME lookup the report's R1 block uses (HUB-3).
   * ABSENT for a first underwrite, a pursuit, or a latest run that did not succeed — never "unchanged".
   */
  previousUnderwrite?: PreviousUnderwrite;
  updatedAt: string;
  /**
   * When the record was last underwritten — NULL when it never has been, which is a real state and
   * not a zero date. Distinct from `updatedAt`, which also moves for a sponsor edit or a state change.
   */
  lastUnderwrittenAt?: string | null;
  /**
   * Documents ingested since that underwrite BEGAN — R8's currency signal.
   *
   * ⚠️ Non-zero means the record's numbers were computed from a document set that has since moved on.
   * That is the condition #653 sat in for twelve runs with nothing on any screen to say so.
   *
   * ⚠️ DOCUMENTS NEW TO THE RECORD (narrowed 2026-10-11): a newer version of a document already on the
   * record is `documentsRevisedSince`, not this. An older api counted every stamp here, revisions included.
   */
  documentsAddedSince?: number;
  /**
   * Documents the latest underwrite could have read that have a NEWER VERSION since (a connector
   * re-save, a version upload) — 0 when never underwritten. Optional: absent is not zero.
   */
  documentsRevisedSince?: number;
  /**
   * Documents that were on the record when that underwrite began and have been WITHDRAWN since
   * (an unrestored withdrawal in force) — 0 when never underwritten. Non-zero means the latest figures still
   * include documents a person has taken off the record. Optional for the same reason as the counts above:
   * absent is not zero.
   */
  documentsWithdrawnSince?: number;
  /**
   * Documents that were WITHDRAWN when that underwrite began and have been RESTORED since (migration
   * 0066) — 0 when never underwritten. Non-zero means the latest figures were computed without
   * documents the next run will judge. Optional: absent is not zero.
   */
  documentsRestoredSince?: number;
  /**
   * The living sponsor profile (#501) — WHO the sponsor is (the SEC EDGAR search key) and WHAT they
   * have done (the corpus Sponsor & Execution judges 20 of the 100 points against).
   *
   * Held on the RECORD rather than on a submission because the autonomous path has no keyboard: a
   * connector-synced document drop re-underwrites with nobody present to type it, so it can only
   * inherit what the record already holds. Null until captured.
   */
  sponsorName: string | null;
  sponsorContext: string | null;
  /**
   * The gate's questions about this record's documents — **THREE STATES, and they must not render
   * alike**: `[…]` questions · `[]` the gate looked and found nothing · **`null` = it could not look**.
   *
   * ⚠️ `null` is also what every run completed before 2026-08-27 returns, because the gate was not
   * persisted until then. Rendering an unchecked run as "no issues" would assert a clean that was
   * never established — the same lie the tier redaction refuses to tell.
   *
   * ⚠️ Optional here only because a client built before the field existed would otherwise be
   * retroactively invalid. The server always sends the key.
   */
  gateFindings?: GateFinding[] | null;
  /**
   * ADR-0077 — what moved since each schedule's or estimate's prior version, from the latest run. Null
   * when it recorded none. Served only on this read: the analysis read and MCP strip it.
   */
  versionDeltas?: VersionDeltasView[] | null;
  /**
   * ADR-0077 D8 — where two of the record's documents disagree, from the latest run. Null when no pair
   * was compared. Served only on this read, as `versionDeltas` is.
   */
  crossDocument?: VersionDeltasView | null;
  /**
   * The proforma's construction loan against its sources and uses, from the latest run. Null when it
   * ties, was not checked, or the reader is not staff. Served only on this read.
   */
  loanTie?: VersionDeltasView | null;
  /** ADR-0069 N27 — the Opportunity Zone check. Staff only. */
  opportunityZone?: VersionDeltasView | null;
}

/**
 * Set or correct the project's living sponsor profile. Omitting a field leaves it untouched; an
 * empty string clears it. Returns the stored values so the caller renders what actually persisted
 * rather than what it hoped it sent.
 */
export async function updateProjectSponsor(
  code: string,
  patch: { sponsorName?: string | null; sponsorContext?: string | null },
): Promise<{ sponsorName: string | null; sponsorContext: string | null }> {
  const res = await request(`/projects/${encodeURIComponent(code)}/sponsor`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (res.status === 403) {
    throw new Error('You don’t have permission to edit this project’s sponsor profile.');
  }
  if (res.status === 404) throw new Error('That project no longer exists in your workspace.');
  if (!res.ok) throw new Error('We couldn’t save the sponsor profile just now. Try again.');
  return (await res.json()) as { sponsorName: string | null; sponsorContext: string | null };
}

/** One immutable governance event on a project's timeline (GET /projects/:code/decisions). */
export interface DecisionRecord {
  id: string;
  /**
   * Every kind `decision_records` admits. ⚠️ This union had drifted to four of six: the endpoint has
   * served `intake` and `promotion` since 0019/0020, and the type claimed it could not.
   */
  kind:
    | 'intake'
    | 'transition'
    | 'crp_approval'
    | 'variance_clearance'
    | 'promotion'
    | 'stop_attestation'
    | 'basis_lock';
  fromState: RecordState | null;
  toState: RecordState | null;
  crp: 'CRP-1' | 'CRP-2' | 'CRP-3' | null;
  actorUserId: string | null;
  /**
   * WHO decided, as a NAME.
   *
   * ⚠️ Null is a REAL STATE — a system-initiated transition (connector-synced re-analysis) has no
   * human, and the Timeline must say "the Engine" rather than invent one. Every other null here is a
   * row written before the column existed.
   */
  actorName?: string | null;
  rationale: string | null;
  /**
   * What the decision rested on. An ATTESTED advance carries `{ basis: 'attestation', verified,
   * carried }` (ADR-0070 D3); read it with {@link attestationOf}, never by shape-guessing at a call site.
   */
  evidence?: unknown;
  /** A carried item written off rather than verified (ADR-0069 A4). */
  writtenOff?: boolean;
  decidedAt: string;
}

/** The item-by-item record of an attested advance, when a decision is one. */
export interface AttestationEvidence {
  releasedCrp: CrpBrief['crp'];
  /** True when every item was carried — allowed, but never allowed to read as an ordinary advance. */
  nothingVerified: boolean;
  verified: { item: string }[];
  /** The two figures are absent on an advance recorded before a price and a cap were required. */
  carried: { item: string; exposure: string; exposureAmount?: number; exposureCap?: number }[];
}

/** The attestation behind a `transition`, or null when it was Engine-driven (or not a transition). */
export function attestationOf(d: DecisionRecord): AttestationEvidence | null {
  const e = d.evidence as (Partial<AttestationEvidence> & { basis?: unknown }) | null | undefined;
  if (d.kind !== 'transition' || !e || e.basis !== 'attestation') return null;
  return {
    releasedCrp: e.releasedCrp as CrpBrief['crp'],
    nothingVerified: e.nothingVerified === true,
    verified: Array.isArray(e.verified) ? e.verified : [],
    carried: Array.isArray(e.carried) ? e.carried : [],
  };
}

/** What a release did with its open items (ADR-0069 A4). `notCarried` is absent for a viewer. */
export function releaseCarryOf(d: DecisionRecord): {
  carried: AttestationEvidence['carried'];
  notCarried: string[];
} | null {
  const e = d.evidence as { carried?: unknown; notCarried?: unknown } | null | undefined;
  if (d.kind !== 'crp_approval' || !e) return null;
  return {
    carried: Array.isArray(e.carried) ? (e.carried as AttestationEvidence['carried']) : [],
    notCarried: Array.isArray(e.notCarried) ? (e.notCarried as string[]) : [],
  };
}

/** One line in a CRP brief — a verified fact, an open item, or a priced exposure. */
export interface CrpBriefItem {
  label: string;
  detail: string;
}

/** The assembled Conditional Release Point brief the approving owner releases against (GET /projects/:code/crp). */
export interface CrpBrief {
  crp: 'CRP-1' | 'CRP-2' | 'CRP-3';
  title: string;
  verified: CrpBriefItem[];
  open: CrpBriefItem[];
  pricedExposure: CrpBriefItem[];
  recommendation: string;
}

/** The living project record, or null on 404 (unknown/other-tenant project) / 401 (session gone). */
export async function getProject(code: string): Promise<ProjectDetail | null> {
  const res = await request(`/projects/${encodeURIComponent(code)}`);
  if (res.status === 404 || res.status === 401) return null;
  if (!res.ok) throw new Error('We couldn’t load this project just now. Refresh to try again.');
  const body = (await res.json()) as { project: ProjectDetail };
  return body.project;
}

/** The project's Decision Record timeline (newest first). Returns [] on 404/401 so a caller can degrade. */
export async function getProjectDecisions(code: string): Promise<DecisionRecord[]> {
  const res = await request(`/projects/${encodeURIComponent(code)}/decisions`);
  if (!res.ok) return [];
  const body = (await res.json()) as { decisions?: DecisionRecord[] };
  return Array.isArray(body.decisions) ? body.decisions : [];
}

// ── Decision disputes — a binding decision contested on the record (ADR-0076) ───────────────────────
// Any Decision Record can be disputed by a staff member, and answered with recorded reasoning by an
// owner or admin. Mirrors `routes/decision-disputes.ts`; permissive where the wire is.

/** How a dispute was answered. `partial` accepts some of the claim. */
export type DisputeResolution = 'accepted' | 'rejected' | 'partial';

export interface DecisionDisputeAnswer {
  resolution: DisputeResolution;
  reasoning: string;
  answeredByName: string;
  /** The person who answered also made the disputed decision (ADR-0076 Q2): allowed, and shown. */
  answeredOwnDecision: boolean;
  answeredAt: string;
}

/** One dispute on the record. `answer: null` IS the open state. */
export interface DecisionDispute {
  id: string;
  decisionId: string;
  decisionKind: DecisionRecord['kind'];
  /** Null for a decision the Engine made. */
  decidedBy: string | null;
  decidedAt: string;
  claim: string;
  disputedByName: string;
  /** WHO filed it, by id — so the answer form is withheld from them. Null if the user was deleted. */
  disputedByUserId: string | null;
  supportingDocumentId: string | null;
  createdAt: string;
  status: 'open' | 'answered';
  answer: DecisionDisputeAnswer | null;
}

export interface DisputeLedger {
  disputes: DecisionDispute[];
  openCount: number;
}

/**
 * The project's dispute ledger, open first.
 *
 * ⚠️ NULL on any failure, never an empty ledger. An empty ledger is a claim that nothing on the record
 * is disputed, and a failed read cannot make it.
 */
export async function getProjectDisputes(code: string): Promise<DisputeLedger | null> {
  try {
    const res = await request(`/projects/${encodeURIComponent(code)}/disputes`);
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<DisputeLedger>;
    if (!Array.isArray(body.disputes)) return null;
    const disputes = body.disputes;
    return { disputes, openCount: body.openCount ?? disputes.filter((d) => !d.answer).length };
  } catch {
    return null;
  }
}

/**
 * The server's own refusal, as a sentence. The dispute routes write their refusals in plain words
 * ("a dispute is answered by someone other than who filed it"), so passing them through tells the
 * reader the real reason. Falls back to `fallback` when the body carries none.
 */
async function disputeRefusal(res: Response, fallback: string): Promise<string> {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  const reason = typeof body?.error === 'string' ? body.error.trim() : '';
  return reason ? `${reason.charAt(0).toUpperCase()}${reason.slice(1)}.` : fallback;
}

/**
 * Dispute one decision. The disputer's NAME is not sent: the server takes it from the session, so the
 * record names who actually acted.
 */
export async function fileDispute(
  code: string,
  decisionId: string,
  input: { claim: string; supportingDocumentId?: string | null },
): Promise<void> {
  const res = await request(
    `/projects/${encodeURIComponent(code)}/decisions/${encodeURIComponent(decisionId)}/disputes`,
    {
      method: 'POST',
      body: JSON.stringify({
        claim: input.claim,
        supportingDocumentId: input.supportingDocumentId ?? null,
      }),
    },
  );
  if (!res.ok) {
    throw new Error(
      await disputeRefusal(res, 'We couldn’t file that dispute just now. Give it another try.'),
    );
  }
}

/** Answer a dispute (owner/admin, server-enforced). Reasoning is required at the route and the database. */
export async function answerDispute(
  code: string,
  decisionId: string,
  disputeId: string,
  input: { resolution: DisputeResolution; reasoning: string },
): Promise<void> {
  const res = await request(
    `/projects/${encodeURIComponent(code)}/decisions/${encodeURIComponent(decisionId)}/disputes/${encodeURIComponent(disputeId)}/answer`,
    { method: 'POST', body: JSON.stringify(input) },
  );
  if (!res.ok) {
    throw new Error(
      await disputeRefusal(res, 'We couldn’t record that answer just now. Give it another try.'),
    );
  }
}

/**
 * A document held by a project record — one row per DOCUMENT, at its newest version.
 *
 * `versionNumber` is the Nexus version this stamp recorded: 1 for a fresh document, N for the Nth
 * update. A document KEEPS its id across versions, which is why an update names the id it supersedes
 * rather than uploading under a new one.
 */
/** Why a withdrawal could not be recorded. Each maps to a sentence the panel shows. */
export type WithdrawFailure = 'forbidden' | 'not_found' | 'already_withdrawn' | 'server_error';

/** Why a restore could not be recorded. Each maps to a sentence the panel shows. */
export type RestoreFailure = 'forbidden' | 'not_found' | 'not_withdrawn' | 'server_error';

export interface ProjectDocument {
  documentId: string;
  filename: string;
  documentType: string | null;
  versionNumber: number;
  lineage: 'new' | 'update';
  /**
   * WHO added it to the record (canon R2). Null on a connector-synced arrival (no human submitted it)
   * and on stamps written before the ledger carried a name (migration 0032) — both are honest states
   * the UI says plainly rather than dressing as "Unknown user".
   *
   * ⚠️ This replaced `submittedBy`, a raw user id that was served here and rendered by nothing. It
   * could not be rendered: nothing on this side of the response can resolve a uuid to a person.
   */
  submittedByName: string | null;
  submittedAt: string;
  /**
   * The ingest STAMP's id — the thing a withdrawal names.
   *
   * Not the document id: a document sits on a record at several versions and canon's second grain
   * withdraws one version while an earlier one serves again, so the verb keys the stamp.
   */
  ingestId: string;
  /** When it left the living view, or null while it is still in it. */
  withdrawnAt: string | null;
  /** WHO said so. Required by the ledger wherever a withdrawal exists. */
  withdrawnByName: string | null;
  /** WHY. Required and non-blank — canon says "with a note" twice. */
  withdrawalNote: string | null;
  /**
   * Whether the document is still in the record's `document_ids`. False for a stamp whose id has left
   * the record — pruned when Nexus no longer had it, replaced by a duplicate's id, removed by hand, or
   * an upload from before #1311 that was never attached. On file; never judged.
   *
   * ⚠️ Optional only because a web build can run briefly ahead of its api. ABSENT is read as the
   * pre-flag behaviour (listed among the current documents) — never as "not judged", which would fold
   * an entire record away on a deploy skew.
   */
  onRecord?: boolean;
  /** On the record AND not withdrawn — read by the underwrite. The server's rule, not ours. */
  judged?: boolean;
}

/** The documents in a project record, newest arrival first. `[]` on 404/401 so a caller can degrade. */
export async function getProjectDocuments(code: string): Promise<ProjectDocument[]> {
  const res = await request(`/projects/${encodeURIComponent(code)}/documents`);
  if (!res.ok) return [];
  const body = (await res.json()) as { documents?: ProjectDocument[] };
  return Array.isArray(body.documents) ? body.documents : [];
}

/** One sheet in the Current Set (ADR-0061 D3/D5). */
export interface DrawingSheet {
  versionId: string;
  sheetNumber: string;
  /** The letter prefix, computed as a grouping key — NEVER read off a title block, never authoritative. */
  discipline: string;
  title: string;
  revision: string;
  fileName: string;
  /**
   * The Nexus document that holds this sheet, or null.
   *
   * 🔴 NULL IS A REAL STATE AND THE PANEL MUST SAY SO. It means the file was never ingested into
   * this project, or that its name matched two ingests carrying DIFFERENT document ids —
   * `resolveSheetDocuments` refuses rather than picking, because here the difference between two
   * candidate documents is the difference between what a subcontractor bid and what they built.
   *
   * ⚠️ STABLE ACROSS VERSIONS. Opening it serves the CURRENT bytes of that document, never
   * "exactly the revision this row records". Right for the Current Set, which IS the newest by
   * definition; NOT sufficient for a permanent citation (ADR-0061 D4, filed as #1027).
   */
  nexusDocumentId: string | null;
  issueLabel: string;
  issueDate: string;
}

export interface CurrentSet {
  sheets: DrawingSheet[];
  sheetCount: number;
  /**
   * How many versions have been superseded.
   *
   * ⚠️ Carried into the UI deliberately: "0 sheets" alone cannot distinguish an EMPTY record from a
   * record whose lineage failed to load, and this is a contract exhibit. The panel says which.
   */
  supersededCount: number;
  /**
   * How many current sheets a renumbered set retired in favour of another number (Document Control
   * 1.19). Shown beside the superseded count: a retired sheet left the set without a revision of its
   * own, and a reader counting sheets must be able to see where it went.
   */
  retiredCount: number;
  /** False when the request failed — so the panel can say "could not load" instead of "no drawings". */
  loaded: boolean;
  /**
   * True when the api REFUSED us (403), as opposed to being unreachable.
   *
   * ⚠️ Kept separate from `loaded` because "you may not read this" and "we could not ask" send a
   * reader to completely different places. The drawing record is staff-only (ADR-0061 D8), so a
   * viewer's denial rendered as "could not be loaded" would look like an outage on a contract
   * exhibit — the same misdiagnosis ADR-0024 D4 suffered for a month behind an opaque `forbidden`.
   */
  forbidden: boolean;
}

/**
 * The Current Set — "the versions no other version supersedes" (D3), as a QUERY, never a stored file.
 *
 * A failure returns `loaded: false` rather than an empty set. An empty drawing record and an
 * unreachable api look identical otherwise, and telling a reader a project has no drawings when we
 * simply could not ask is the kind of quiet wrong answer this record cannot afford.
 */
export async function getCurrentSet(code: string): Promise<CurrentSet> {
  const unavailable: CurrentSet = {
    sheets: [],
    sheetCount: 0,
    supersededCount: 0,
    retiredCount: 0,
    loaded: false,
    forbidden: false,
  };
  /*
    ⚠️ A THROWN fetch is a failure too, and it was not handled. `request()` REJECTS when the network
    is unreachable — it does not return a non-ok response — so without this the promise rejected
    unhandled and the panel sat on "Loading…" forever. The function's whole contract is that a failure
    is reported as `loaded: false` so the UI can distinguish it from an empty record; a rejection here
    silently broke exactly that distinction.
  */
  let res: Response;
  try {
    res = await request(`/projects/${encodeURIComponent(code)}/drawings/current`);
  } catch {
    return unavailable;
  }
  if (!res.ok) return { ...unavailable, forbidden: res.status === 403 };
  const body = (await res.json()) as Partial<CurrentSet> & { sheets?: DrawingSheet[] };
  const sheets = Array.isArray(body.sheets) ? body.sheets : [];
  return {
    sheets,
    sheetCount: typeof body.sheetCount === 'number' ? body.sheetCount : sheets.length,
    supersededCount: typeof body.supersededCount === 'number' ? body.supersededCount : 0,
    retiredCount: typeof body.retiredCount === 'number' ? body.retiredCount : 0,
    loaded: true,
    forbidden: false,
  };
}

// ── The set as filed (ADR-0061 D3) ────────────────────────────────────────────────────────────────

/** One issue on the drawing record, in the record's FILING order (Brad, 2026-09-24). */
export interface FiledIssue {
  id: string;
  label: string;
  /** Canon's printed issue date — TEXT, never parsed (D2). */
  issueDate: string;
  filedAt: string;
  /** Canon's phase AS STATED (A3 S6); null = not stated. Optional: an older API omits it. */
  phase?: string | null;
  /** Non-null marks a PRICED ANCHOR — the pricing reference money was based on. */
  pricingRef?: string | null;
}

/** "· DD · PRICED (GMP)" — the small label beside an issue; empty when neither is stated. */
export function issueMarks(issue: Pick<FiledIssue, 'phase' | 'pricingRef'>): string {
  const parts: string[] = [];
  if (issue.phase) parts.push(issue.phase);
  if (issue.pricingRef) parts.push(`PRICED (${issue.pricingRef})`);
  return parts.map((p) => ` · ${p}`).join('');
}

/** A sheet in an as-of set. No document id, deliberately: until D4 a link would serve the CURRENT file. */
export interface AsOfSheet {
  versionId: string;
  sheetNumber: string;
  discipline: string;
  title: string;
  revision: string;
  fileName: string;
  issueLabel: string;
  /**
   * Whether the record can find this sheet's document — so whether its as-filed link can work. No
   * document id is sent (ADR-0061 D4): the link is keyed by `versionId`. Absent from an older api ⇒ false.
   */
  linked?: boolean;
}

export interface SetAsFiled {
  asOf: FiledIssue | null;
  sheets: AsOfSheet[];
  loaded: boolean;
  forbidden: boolean;
}

/** The record's issues in filing order; `null` when the request failed (not "no issues"). */
export async function getFiledIssues(
  code: string,
): Promise<{ issues: FiledIssue[]; forbidden: boolean } | null> {
  let res: Response;
  try {
    res = await request(`/projects/${encodeURIComponent(code)}/drawings/issues`);
  } catch {
    return null;
  }
  if (res.status === 403) return { issues: [], forbidden: true };
  if (!res.ok) return null;
  const body = (await res.json()) as { issues?: FiledIssue[] };
  return { issues: Array.isArray(body.issues) ? body.issues : [], forbidden: false };
}

/** The set as the record stood after one issue was filed. A failure is `loaded: false`, never empty. */
export async function getSetAsFiled(code: string, issueId: string): Promise<SetAsFiled> {
  const unavailable: SetAsFiled = { asOf: null, sheets: [], loaded: false, forbidden: false };
  let res: Response;
  try {
    res = await request(
      `/projects/${encodeURIComponent(code)}/drawings/as-of/${encodeURIComponent(issueId)}`,
    );
  } catch {
    return unavailable;
  }
  if (!res.ok) return { ...unavailable, forbidden: res.status === 403 };
  const body = (await res.json()) as { asOf?: FiledIssue; sheets?: AsOfSheet[] };
  return {
    asOf: body.asOf ?? null,
    sheets: Array.isArray(body.sheets) ? body.sheets : [],
    loaded: true,
    forbidden: false,
  };
}

// ── Ask the record (ADR-0045) ─────────────────────────────────────────────────────────────────────

/** One source a printed sentence cites. `documentId` only for a document passage. */
export interface AskSource {
  ref: string;
  label: string;
  documentId?: string;
}

export interface AskSentence {
  sentence: string;
  sources: AskSource[];
}

/**
 * What asking produced — the route's three outcomes, plus the four ways the question never reached it.
 *
 * ⚠️ SEVEN KINDS, AND THE PANEL MUST TELL THEM APART. "Nothing on this record answers that", "the
 * Engine failed", "this account may not ask" and "the api could not be reached" are four different
 * facts, and a person acts differently on each. Collapsing any two tells someone the record lacks an
 * answer it may well hold — the failure Stage 3.5 rules against.
 */
export type AskReply =
  | { kind: 'answered'; answers: AskSentence[]; withheld: number; leftOut: string[] }
  | {
      kind: 'refused';
      refusal: { kind: string; text: string; what: string | null };
      leftOut: string[];
    }
  | { kind: 'engine_failed'; text: string; leftOut: string[] }
  | { kind: 'forbidden' }
  | { kind: 'signed-out' }
  | { kind: 'rate_limited' }
  | { kind: 'error'; message: string };

const isString = (v: unknown): v is string => typeof v === 'string';

/**
 * Ask one project record a question — `POST /projects/:code/ask`.
 *
 * NEVER THROWS: a network failure is `error`, for the reason `getCurrentSet` above learned the hard way
 * — a rejected promise leaves a panel on its loading state forever. A 401 has already been through
 * `request`'s refresh-and-replay, so one that survives it means the session is genuinely gone.
 */
export async function askProject(code: string, question: string): Promise<AskReply> {
  let res: Response;
  try {
    res = await request(`/projects/${encodeURIComponent(code)}/ask`, {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  } catch {
    return { kind: 'error', message: 'The api could not be reached.' };
  }
  if (res.status === 401) return { kind: 'signed-out' };
  if (res.status === 403) return { kind: 'forbidden' };
  if (res.status === 429) return { kind: 'rate_limited' };
  if (!res.ok) return { kind: 'error', message: `The api answered ${res.status}.` };

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { kind: 'error', message: 'The api answered with something that was not a reply.' };
  }
  /*
    ⚠️ BELT AND BRACES. `readAskReply` is written to be total, and this catch is what keeps the
    "never throws" promise true if it ever is not. The red-team found the first version threw on a 200
    whose body was `null` — and the panel then showed the question with NOTHING under it, which a
    reader takes as "no answer".
  */
  try {
    return readAskReply(body);
  } catch {
    return { kind: 'error', message: 'The api answered in a shape this page does not recognise.' };
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const UNRECOGNISED: AskReply = {
  kind: 'error',
  message: 'The api answered in a shape this page does not recognise.',
};

/**
 * Read one reply, TOTALLY — every field checked, nothing cast and dereferenced.
 *
 * 🔴 A SENTENCE WITH NO VALID SOURCE IS DROPPED AND COUNTED, never shown bare. The server already
 * refuses to print one; this is the last step before the screen, and "every sentence names its source"
 * has to hold here too or it holds nowhere the reader can see. And an "answer" with nothing left in it
 * is an ERROR — an empty answered state renders as a blank, and a blank reads as "no answer".
 */
function readAskReply(body: unknown): AskReply {
  // A shape this client does not know is an ERROR, never read as a refusal — the panel must not
  // announce that the record lacks something because we could not parse what came back.
  if (!isRecord(body)) return UNRECOGNISED;
  const leftOut = Array.isArray(body['leftOut']) ? body['leftOut'].filter(isString) : [];

  if (body['outcome'] === 'answered' && Array.isArray(body['answers'])) {
    const answers: AskSentence[] = [];
    let dropped = 0;
    for (const a of body['answers'] as unknown[]) {
      const rawSources: unknown[] = isRecord(a) && Array.isArray(a['sources']) ? a['sources'] : [];
      const sources = rawSources.flatMap((x): AskSource[] =>
        isRecord(x) && isString(x['ref']) && isString(x['label'])
          ? [
              {
                ref: x['ref'],
                label: x['label'],
                ...(isString(x['documentId']) ? { documentId: x['documentId'] } : {}),
              },
            ]
          : [],
      );
      if (!isRecord(a) || !isString(a['sentence']) || sources.length === 0) {
        dropped += 1;
        continue;
      }
      answers.push({ sentence: a['sentence'], sources });
    }
    if (answers.length === 0) return UNRECOGNISED;
    const withheld = (typeof body['withheld'] === 'number' ? body['withheld'] : 0) + dropped;
    return { kind: 'answered', answers, withheld, leftOut };
  }
  if (body['outcome'] === 'refused') {
    const r = body['refusal'];
    if (isRecord(r) && isString(r['kind']) && isString(r['text'])) {
      return {
        kind: 'refused',
        refusal: { kind: r['kind'], text: r['text'], what: isString(r['what']) ? r['what'] : null },
        leftOut,
      };
    }
  }
  if (body['outcome'] === 'engine_failed' && isString(body['text'])) {
    return { kind: 'engine_failed', text: body['text'], leftOut };
  }
  return UNRECOGNISED;
}

/**
 * One handover: a file served to a person through the Hub (canon R4). `document_ingests` says what came
 * IN; this is what went OUT.
 */
export interface DocumentRetrieval {
  id: string;
  documentId: string;
  /** The name the RECORD cites the document by, captured at retrieval — stable across a supersede. */
  filename: string;
  /**
   * WHO took it. Null ONLY on rows written before the ledger stamped a name (migration 0030) — a
   * genuinely unrecorded actor, which the UI must say plainly rather than dressing as "Unknown user".
   */
  retrievedByName: string | null;
  retrievedAt: string;
  /**
   * ADR-0061 D4 — the version of the file that was handed over. NULL = NOT RECORDED (every row before
   * migration 0067, or a Nexus that did not name the file). Never read NULL as "the current version".
   * Optional because an api older than 0067 omits it.
   */
  servedVersionNumber?: number | null;
  /** That file's sha256, lowercase hex. NULL = not recorded, or a file Nexus never hashed. */
  servedSha256?: string | null;
  /** ADR-0061 D6 — TRUE when that exact file was asked for (the version-pinned route). */
  pinned?: boolean;
}

export interface ProjectRetrievals {
  retrievals: DocumentRetrieval[];
  /** True when older handovers exist beyond this page. A truncated audit list must SAY it is truncated. */
  truncated: boolean;
}

/** The record's retrieval ledger, newest first. Empty (and not truncated) on 404/401 — degrade quietly. */
export async function getProjectRetrievals(code: string): Promise<ProjectRetrievals> {
  const res = await request(`/projects/${encodeURIComponent(code)}/retrievals`);
  if (!res.ok) return { retrievals: [], truncated: false };
  const body = (await res.json()) as Partial<ProjectRetrievals>;
  return {
    retrievals: Array.isArray(body.retrievals) ? body.retrievals : [],
    truncated: body.truncated === true,
  };
}

/**
 * One file passing through the governed ingest gate, with the gate's two questions answered: what KIND
 * of document this is, and whether it is `new` or an `update` to a document already on the record.
 *
 * `targetDocumentId` is required when `lineage` is `update` — an update supersedes VISIBLY, naming what
 * it replaces, and the api refuses an update that names nothing.
 */
export interface GateDocument {
  filename: string;
  contentType: string;
  contentBase64: string;
  documentType: string | null;
  lineage: 'new' | 'update';
  targetDocumentId: string | null;
}

export type IngestFailure =
  | 'forbidden'
  | 'not_found'
  | 'closed'
  | 'update_target_not_on_record'
  | 'too_large'
  | 'unavailable'
  | 'server_error';

/**
 * Push documents through the PEE governed gate (`POST /projects/:code/documents`).
 *
 * ⚠️ NOT the same door as an upload from the home page. That one posts to `/analyses/from-documents`
 * — the customer Deal Analyzer path, which carries a folder-routing hint and writes NO stamp. This one
 * ingests INTO an existing record and stamps `document_ingests` with who, when, type and lineage
 * (canon R6, "no silent saves"). It never creates a project: a code nobody created in the PEE is a 404.
 */
/**
 * Withdraw a document from the record's living view (canon's correction verbs).
 *
 * 🔴 NOT A DELETE. This appends a withdrawal event; the bytes, the ingest stamp, the lineage and the
 * attribution are untouched and the document stays retrievable. The api enforces the note as well as
 * the database does — the route gives a person a usable refusal, the CHECK makes the rule true for
 * every other caller there will ever be.
 */
export async function withdrawProjectDocument(
  code: string,
  ingestId: string,
  note: string,
): Promise<{ ok: true } | { ok: false; reason: WithdrawFailure }> {
  try {
    const res = await request(
      `/projects/${encodeURIComponent(code)}/documents/${encodeURIComponent(ingestId)}/withdraw`,
      { method: 'POST', body: JSON.stringify({ note }) },
    );
    if (res.ok) return { ok: true };
    if (res.status === 403) return { ok: false, reason: 'forbidden' };
    if (res.status === 404) return { ok: false, reason: 'not_found' };
    // 409 is the one a second caller can legitimately hit — "already withdrawn" is decided in the
    // database under a per-document lock (migration 0066), so the race resolves there, not here.
    if (res.status === 409) return { ok: false, reason: 'already_withdrawn' };
    return { ok: false, reason: 'server_error' };
  } catch {
    return { ok: false, reason: 'server_error' };
  }
}

/**
 * Restore a withdrawn document to the record's living view — the ONLY way one comes back (Brad,
 * 2026-10-10). A re-upload of the same file leaves it withdrawn; a person decides, with a note, and the
 * record keeps both the withdrawal and the restore. `ingestId` is the panel row's stamp; the api
 * restores the DOCUMENT it belongs to.
 */
export async function restoreProjectDocument(
  code: string,
  ingestId: string,
  note: string,
): Promise<{ ok: true } | { ok: false; reason: RestoreFailure }> {
  try {
    const res = await request(
      `/projects/${encodeURIComponent(code)}/documents/${encodeURIComponent(ingestId)}/restore`,
      { method: 'POST', body: JSON.stringify({ note }) },
    );
    if (res.ok) return { ok: true };
    if (res.status === 403) return { ok: false, reason: 'forbidden' };
    if (res.status === 404) return { ok: false, reason: 'not_found' };
    // Someone else restored it between the page loading and the press.
    if (res.status === 409) return { ok: false, reason: 'not_withdrawn' };
    return { ok: false, reason: 'server_error' };
  } catch {
    return { ok: false, reason: 'server_error' };
  }
}

/**
 * Why a re-underwrite was not started. Each maps to a sentence the panel shows — except `in_flight`,
 * which carries the run already going so the panel can take the reader to it.
 */
export type ReunderwriteFailure =
  | 'forbidden'
  | 'not_found'
  | 'in_flight'
  | 'nothing_to_judge'
  | 'not_living'
  | 'no_project_code'
  | 'unavailable'
  | 'server_error';

/**
 * Re-underwrite a record over the documents it currently judges (`POST /projects/:code/reunderwrite`).
 *
 * The staff verb a withdrawal was missing: taking a document out of the record queues nothing, so
 * until this the change only counted on the next run somebody or something else happened to start.
 * It SPENDS — a paid underwrite of the whole set — which is why the panel confirms before calling it.
 *
 * ⚠️ A 409 is read by its machine `reason`, never its sentence. `in_flight` comes back WITH the id of
 * the run already going: the reader asked to see the record re-judged, and that run is it.
 */
export async function reunderwriteProject(
  code: string,
): Promise<
  { ok: true; analysisId: string } | { ok: false; reason: ReunderwriteFailure; analysisId?: string }
> {
  try {
    const res = await request(`/projects/${encodeURIComponent(code)}/reunderwrite`, {
      method: 'POST',
    });
    const body = (await res.json().catch(() => ({}))) as {
      id?: string;
      reason?: string;
      analysisId?: string;
    };
    if (res.ok) {
      return body.id ? { ok: true, analysisId: body.id } : { ok: false, reason: 'server_error' };
    }
    if (res.status === 403) return { ok: false, reason: 'forbidden' };
    if (res.status === 404) return { ok: false, reason: 'not_found' };
    if (res.status === 409) {
      if (body.reason === 'in_flight') {
        return body.analysisId
          ? { ok: false, reason: 'in_flight', analysisId: body.analysisId }
          : { ok: false, reason: 'in_flight' };
      }
      if (body.reason === 'nothing_to_judge') return { ok: false, reason: 'nothing_to_judge' };
      if (body.reason === 'no_project_code') return { ok: false, reason: 'no_project_code' };
      // `not_living`, and any 409 this client does not know yet — the record will not take a run.
      return { ok: false, reason: 'not_living' };
    }
    if (res.status === 503) return { ok: false, reason: 'unavailable' };
    return { ok: false, reason: 'server_error' };
  } catch {
    return { ok: false, reason: 'server_error' };
  }
}

/** Why a report request was not accepted. Each maps to a sentence the panel shows. */
export type ReportRequestFailure =
  | 'forbidden'
  | 'tenant_not_allowed'
  | 'not_found'
  | 'no_published_insight'
  | 'insight_not_found'
  | 'nexus_timeout'
  | 'rate_limited'
  | 'nexus_tenant_unverified'
  | 'unavailable'
  | 'nexus_error'
  | 'server_error';

/**
 * Ask Nexus to render the GGP deal-report PDF for the record's latest published insight
 * (`POST /projects/:code/report`).
 *
 * ⚠️ SUCCESS MEANS "REQUESTED", NOT "THE REPORT EXISTS". The server answers 202 only when Nexus accepted
 * the render; the PDF is filed into the project's workspace when Nexus's job finishes, which this call
 * cannot see. A 202 without an insight id is treated as a server error rather than a success.
 */
export async function requestProjectReport(
  code: string,
): Promise<
  | { ok: true; insightId: string; version: number | null }
  | { ok: false; reason: ReportRequestFailure }
> {
  try {
    const res = await request(`/projects/${encodeURIComponent(code)}/report`, { method: 'POST' });
    const body = (await res.json().catch(() => ({}))) as {
      insightId?: unknown;
      version?: unknown;
      reason?: unknown;
    };
    if (res.status === 202) {
      return typeof body.insightId === 'string' && body.insightId !== ''
        ? {
            ok: true,
            insightId: body.insightId,
            version: typeof body.version === 'number' ? body.version : null,
          }
        : { ok: false, reason: 'server_error' };
    }
    if (res.status === 403) {
      return {
        ok: false,
        reason: body.reason === 'tenant_not_allowed' ? 'tenant_not_allowed' : 'forbidden',
      };
    }
    if (res.status === 404) return { ok: false, reason: 'not_found' };
    if (res.status === 409) {
      return {
        ok: false,
        reason: body.reason === 'insight_not_found' ? 'insight_not_found' : 'no_published_insight',
      };
    }
    // OUR route's own rate limit (5/min) — a stuck or impatient button, said as such.
    if (res.status === 429) return { ok: false, reason: 'rate_limited' };
    if (res.status === 504) return { ok: false, reason: 'nexus_timeout' };
    if (res.status === 503) return { ok: false, reason: 'unavailable' };
    if (res.status === 502) {
      return {
        ok: false,
        reason:
          body.reason === 'nexus_tenant_unverified' ? 'nexus_tenant_unverified' : 'nexus_error',
      };
    }
    return { ok: false, reason: 'server_error' };
  } catch {
    return { ok: false, reason: 'server_error' };
  }
}

export async function ingestProjectDocuments(
  code: string,
  documents: GateDocument[],
): Promise<{ ok: true; analysisId?: string } | { ok: false; reason: IngestFailure }> {
  try {
    const res = await request(`/projects/${encodeURIComponent(code)}/documents`, {
      method: 'POST',
      body: JSON.stringify({ documents }),
    });
    if (res.ok) {
      // 202 + `{ id, status }` — the run that will re-underwrite the record over its whole document
      // set. Returned so the caller can follow the deal updating rather than leaving the reader to
      // wonder whether anything happened.
      const body = (await res.json().catch(() => ({}))) as { id?: string };
      return body.id ? { ok: true, analysisId: body.id } : { ok: true };
    }
    if (res.status === 403) return { ok: false, reason: 'forbidden' };
    if (res.status === 404) return { ok: false, reason: 'not_found' };
    if (res.status === 409) {
      /*
        TWO 409s ON THIS DOOR, and only one means "closed". The api names the other with a `reason`
        (projects.ts, #1311): an update aimed at a document that is not on this record. Mapping it to
        "finalized" would send the reader looking for a cause that is not there.
      */
      const body = (await res.json().catch(() => ({}))) as { reason?: unknown };
      return {
        ok: false,
        reason: body.reason === 'update_target_not_on_record' ? body.reason : 'closed',
      };
    }
    if (res.status === 413) return { ok: false, reason: 'too_large' };
    if (res.status === 503) return { ok: false, reason: 'unavailable' };
    return { ok: false, reason: 'server_error' };
  } catch {
    return { ok: false, reason: 'unavailable' };
  }
}

/** One lock in a project's basis chain (ADR-0071). */
export interface BasisLockView {
  id: string;
  /** The approved inputs, frozen at the lock. */
  dealInputs: DealInputs;
  lockedByName: string;
  reason: string;
  lockedAt: string;
}

/** A project's locked basis: the one that governs, and every lock before it (newest first). */
export interface BasisState {
  governing: BasisLockView | null;
  chain: BasisLockView[];
  /** What a lock would freeze right now — the latest run's inputs; null before any underwrite. */
  current: { analysisId: string; dealInputs: DealInputs } | null;
}

/** The project's locked basis, or null when it can't be read (the card degrades, the page does not). */
export async function getBasis(code: string): Promise<BasisState | null> {
  const res = await request(`/projects/${encodeURIComponent(code)}/basis`);
  if (!res.ok) return null;
  return (await res.json()) as BasisState;
}

/** Raised when a lock is refused — no underwrite, a stale view of the chain (409), or no reason (400). */
export class BasisLockError extends Error {}

/**
 * Lock (or re-lock) the basis — owner only, server-enforced. It names the run whose numbers the owner
 * was shown (refused if another has landed since) and, on a re-lock, the lock it replaces; the earlier
 * one stays on the record.
 */
export async function lockBasis(
  code: string,
  reason: string,
  analysisId: string,
  supersedes: string | null,
): Promise<void> {
  const res = await request(`/projects/${encodeURIComponent(code)}/basis/lock`, {
    method: 'POST',
    body: JSON.stringify({ reason, analysisId, supersedes }),
  });
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (res.status === 403) throw new BasisLockError('Only the project owner can lock the basis.');
  throw new BasisLockError(
    typeof body.error === 'string' ? body.error : 'We couldn’t lock the basis just now.',
  );
}

/** The next attested advance offered at a record's state (ADR-0070 D3/D4). */
export interface AdvanceOffer {
  from: RecordState;
  to: RecordState;
  /** The release whose spend must precede entering `to`. */
  requiredCrp: CrpBrief['crp'];
  spendReleased: boolean;
  /** Exactly what the owner attests — every item, verified or carried. */
  checklist: CrpBriefItem[];
}

/** What can happen at a record's current state (GET /projects/:code/crp). */
export interface CrpState {
  state: RecordState;
  /** Releases already made on this record — each happens once. */
  released: CrpBrief['crp'][];
  /**
   * Releases still open at this state, each with the brief it is decided from, and the release it
   * requires first (CRP-2 needs CRP-1; ruled 2026-09-28).
   */
  releases: {
    crp: CrpBrief['crp'];
    brief: CrpBrief;
    requires: CrpBrief['crp'] | null;
    prerequisiteMet: boolean;
    /** CRP-3 needs a locked basis first (ADR-0071 D5); true when not required or already locked. */
    lockedBasisMet: boolean;
  }[];
  advance: AdvanceOffer | null;
}

/**
 * The releases open at the project's state and the next attested advance, or null when the project
 * doesn't exist or the read failed (the panel degrades rather than blocking the page).
 */
export async function getCrpState(code: string): Promise<CrpState | null> {
  const res = await request(`/projects/${encodeURIComponent(code)}/crp`);
  if (!res.ok) return null;
  return (await res.json()) as CrpState;
}

/** Raised when the caller's role isn't permitted to release a CRP (server-enforced 403). */
export class CrpForbiddenError extends Error {}

/** Raised when a release or an advance conflicts with the record's current state (409). */
export class CrpConflictError extends Error {}

/** Raised when an attestation is malformed — an item missing, unknown, or carried without a price (400). */
export class AttestationInvalidError extends Error {}

/** An open item the owner carries past a release, named as {@link briefItemName} names it. */
export interface CarriedReleaseItem {
  item: string;
  exposure: string;
  exposureAmount: number | null;
  exposureCap: number | null;
}

/** An open item's name on the record. Kept in lockstep with @ggpathos/lifecycle's `briefItemName`. */
export function briefItemName(item: { label: string; detail: string }): string {
  return `${item.label}: ${item.detail}`;
}

/**
 * Release the named Conditional Release Point → one immutable `crp_approval` Decision Record approving
 * spend on the next phase (owner/admin only, server-enforced). A release NEVER moves the record's state
 * (ADR-0070 D2). Throws {@link CrpForbiddenError} on 403 and {@link CrpConflictError} on 409 (not
 * releasable at this state, or already released).
 */
export async function approveCrp(
  code: string,
  crp: CrpBrief['crp'],
  rationale?: string | null,
  carried: readonly CarriedReleaseItem[] = [],
  shown: readonly string[] = [],
): Promise<{ id: string; crp: CrpBrief['crp'] }> {
  const res = await request(`/projects/${encodeURIComponent(code)}/crp/approve`, {
    method: 'POST',
    body: JSON.stringify({ crp, rationale: rationale ?? null, carried, shown }),
  });
  if (res.status === 403) {
    throw new CrpForbiddenError('Only an owner or admin can release spend.');
  }
  if (res.status === 400) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new AttestationInvalidError(
      typeof body.error === 'string' ? body.error : 'Check the carried items and try again.',
    );
  }
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new CrpConflictError(
      typeof body.error === 'string' ? body.error : 'This release is no longer available.',
    );
  }
  if (res.status === 404) throw new Error('That project no longer exists.');
  if (!res.ok)
    throw new Error('We couldn’t record the release just now. Give it another try in a moment.');
  return (await res.json()) as { id: string; crp: CrpBrief['crp'] };
}

/** One checklist item as the owner attests it. */
export interface AttestedItemInput {
  label: string;
  disposition: 'verified' | 'carried';
  /** Required when carried: the named downside of proceeding without it. */
  exposure?: string | null;
  /** Required when carried: the price of that downside, and the most GGP will carry, in dollars. */
  exposureAmount?: number | null;
  exposureCap?: number | null;
}

/**
 * Attest the current phase and advance the record (owner/admin only, server-enforced). Every checklist
 * item must be listed once, verified or carried; a carried item opens a live Flag. Throws
 * {@link CrpForbiddenError} (403), {@link AttestationInvalidError} (400) or {@link CrpConflictError} (409 —
 * the spend isn't released, a Stop&Fix is open, or the record moved).
 */
export async function advanceProject(
  code: string,
  fromState: RecordState,
  items: AttestedItemInput[],
  rationale?: string | null,
): Promise<{ id: string; from: RecordState; to: RecordState; carriedVarianceIds: string[] }> {
  const res = await request(`/projects/${encodeURIComponent(code)}/advance`, {
    method: 'POST',
    body: JSON.stringify({ fromState, items, rationale: rationale ?? null }),
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  const message = typeof body.error === 'string' ? body.error : null;
  if (res.status === 403) throw new CrpForbiddenError('Only an owner or admin can attest a phase.');
  if (res.status === 400)
    throw new AttestationInvalidError(message ?? 'The attestation is incomplete.');
  if (res.status === 409)
    throw new CrpConflictError(message ?? 'The record can’t advance right now.');
  if (res.status === 404) throw new Error('That project no longer exists.');
  if (!res.ok) throw new Error('We couldn’t record the advance just now. Give it another try.');
  return body as unknown as {
    id: string;
    from: RecordState;
    to: RecordState;
    carriedVarianceIds: string[];
  };
}

// ── The Variance Response Ladder (ADR-0028 D6 / FR-M3-03) ────────────────────────────────
// The record catches drift on re-verification and can HARD-BLOCK an advance with a Stop&Fix. This
// surface lists a project's variances and lets an owner/admin clear one (which unfreezes the CRP release).

/** One persisted variance on a project — a `flag` (surfaced) or a `stop_fix` (blocks advancement). */
export interface Variance {
  id: string;
  category: string;
  level: 'flag' | 'stop_fix';
  status: 'open' | 'cleared';
  summary: string;
  detail?: unknown;
  magnitudePct?: number | null;
  openedAt: string;
  clearedAt?: string | null;
  /** `written_off` when a carried item was written off rather than verified. The figures stay staff only. */
  closedAs?: 'written_off' | null;
  /**
   * WHO cleared it, as a NAME — the only form of the answer this side of the wire can use.
   *
   * ⚠️ `clearedBy` (the raw user id) used to sit beside this and was read by nothing. It could not be
   * read: nothing here resolves a uuid to a person. #680 added the name and left the id on the
   * payload; the orphan-field sweep found it still there.
   */
  clearedByName?: string | null;
  /**
   * What a person's clear DOES to this finding, served by the api (`clearEffectOf`, engine) so the
   * Clear step's copy cannot promise what the system does not do (#1155):
   * - `retires` — the workers never raise the same move again (ADR-0048 §9);
   * - `ends_finding` — a stop opened from a CONFIRMED finding: never re-opened from it, and a hazard
   *   that returns must be recorded as a new finding (ADR-0063 D3);
   * - `may_return` — a later run can raise it again.
   *
   * ⚠️ ABSENT MAKES NO PROMISE. An older server does not send it, and each of the three readings is
   * false for some row, so the step then says only that the finding closes.
   */
  clearEffect?: 'retires' | 'retires_band' | 'ends_finding' | 'may_return';
  /**
   * WHO owns the remediation, stamped at the moment of trigger (canon GT §4.4).
   *
   * ⚠️ NULL IS THE INTERESTING CASE and must render as a stated gap, never as blank — it means the
   * project names no Lead Owner, so this finding has nobody on the hook. Canon: "a stop without an
   * owner and a deadline is not discipline; it is paralysis with a dashboard."
   */
  ownerName?: string | null;
  /** THE CLOCK — when the response is due. Set on every row written since migration 0038. */
  dueAt?: string | null;
}

/**
 * The project's POSTURE — the severest OPEN rung, plus whether anyone is on it. Computed server-side
 * by the engine so the client renders a verdict rather than deriving one.
 */
export interface VariancePosture {
  rung: 'clear' | 'flag' | 'stop_fix';
  open: number;
  openStopFix: number;
  overdue: number;
  unowned: number;
  unclocked: number;
}

/** A project's variances (open first) and its posture. Degrades to empty/clear on 404/401. */
export interface VarianceReport {
  variances: Variance[];
  posture: VariancePosture;
}

/** The posture of a project we could not load — indistinguishable in shape from a genuinely clean one,
 *  which is why the caller renders the panel only when there is something to say. */
const CLEAR_POSTURE: VariancePosture = {
  rung: 'clear',
  open: 0,
  openStopFix: 0,
  overdue: 0,
  unowned: 0,
  unclocked: 0,
};

/** The project's variances (open first). Returns an empty report on 404/401 so the caller degrades. */
export async function getProjectVariances(code: string): Promise<VarianceReport> {
  const res = await request(`/projects/${encodeURIComponent(code)}/variances`);
  if (!res.ok) return { variances: [], posture: CLEAR_POSTURE };
  const body = (await res.json()) as { variances?: Variance[]; posture?: VariancePosture };
  return {
    variances: Array.isArray(body.variances) ? body.variances : [],
    posture: body.posture ?? CLEAR_POSTURE,
  };
}

// ── Record facts — provisional-then-confirmed (ADR-0044) ────────────────────────────────────────────
// A claim attached to a project record that knows HOW STRONG it is and WHAT WOULD SETTLE it. One
// mechanism for two things Chad tied together on 2026-08-13: a restriction fact (the affordable unit
// count the architect stated) and a drawing-register finding. The boundary between them is the presence
// of an owner and a clock, not a second table.

/** What class of claim this is. A new kind is a new value, never a new table — that is the ruling. */
export type RecordFactKind =
  | 'restriction'
  | 'register_finding'
  | 'tracking_log_figure'
  | 'open_item'
  // GGP's own cost estimates (ADR-0069 N14). The default facts read never returns one — staff read
  // them through `GET /cost-history` — but the union mirrors `@ggpathos/db`'s so the two cannot drift.
  | 'construction_cost'
  // A person's statement pinning `financingProgram` or `isLIHTC` on every underwrite (ADR-0069 N27).
  | 'deal_fact'
  // Whether a technical-review pass asked its protected question (#1379). Written by the worker only,
  // the default facts read returns it apart, as `reviewCoverage`, never in `facts`.
  | 'review_coverage'
  // One tracked quantity at one drawing issue, from the design-review register (ADR-0069 A3 S8).
  // Read only through `?kind=tracked_quantity`. The default read leaves it out.
  | 'tracked_quantity';

/**
 * How strong the claim is. `stated` — someone said it. `disclosed` — it arrived through a channel we
 * can name. `confirmed` — the settling event happened.
 *
 * ⚠️ NOT the variance ladder. A fact is not a variance: every `variance_records` row means attention,
 * and a housekeeping fact has no rung and forces no Stop.
 */
export type RecordFactTier = 'stated' | 'disclosed' | 'confirmed';

/** One CURRENT claim on the record — the newest assertion for its `(kind, key)`. */
export interface RecordFactView {
  id: string;
  kind: RecordFactKind;
  /** What the claim is ABOUT — a field name, or the register's permanent finding id. */
  key: string;
  /** The claim itself. Shape belongs to the `kind`; the panel renders it as given. */
  value: unknown;
  /** WHO stated it ("the architect"). ⚠️ Usually not one of our users, and never the recorder. */
  source: string;
  /** HOW it reached us ("drawing-review meeting", "OM p.14"). */
  arrivalPath: string;
  tier: RecordFactTier;
  /** The event that will confirm or refute it. */
  settlesOn?: string | null;
  /** When an unconfirmed claim goes stale rather than quietly hardening into a fact. */
  expiresAt?: string | null;
  /** The person it needs. Null for pure housekeeping — null exactly when `dueAt` is. */
  ownerName?: string | null;
  dueAt?: string | null;
  /** WHO put it on the record. Null on a system-entered fact. */
  recordedByName?: string | null;
  recordedAt: string;
  /** The server's verdict (Hub Phase 3a). Optional so an older API response still renders. */
  resolution?: FactResolutionView | null;
  /**
   * Present only on a review finding whose EVERY source document was withdrawn (Brad, 2026-10-02):
   * shown "source withdrawn", never hidden; a person decides. Absent otherwise, and always absent on
   * an unlinked finding.
   */
  sourceWithdrawal?: SourceWithdrawalView;
}

export interface SourceWithdrawalView {
  sourceCount: number;
  withdrawn: { documentId: string; filename: string | null }[];
}

/**
 * `unowned` — housekeeping; nothing is needed. `owed` / `overdue` — a RESOLVED date.
 * `unresolved` — owned, but the clock or the party could not be resolved.
 *
 * ⚠️ `unresolved` IS NOT `not overdue`, and the UI must never render it as reassurance. An item
 * nobody can schedule is not an item that is fine; it is one the record cannot answer for yet.
 */
export type FactResolutionState = 'unowned' | 'owed' | 'overdue' | 'unresolved' | 'closed';

export interface FactResolutionReasonView {
  code: string;
  /**
   * The exact register text that failed to resolve — what a human must add to the calendar. ABSENT for
   * a reader without `register:read` (a client): it quotes the register's needed-by and owner text.
   */
  subject?: string;
  detail?: string;
}

export interface FactResolutionView {
  state: FactResolutionState;
  dueDate?: string | null;
  dueFrom?: 'due-date' | 'milestone' | null;
  reasons?: FactResolutionReasonView[];
}

/** Counts plus the seeding worklist — what the calendar and directory are still missing. */
export interface FactResolutionSummary {
  owed: number;
  overdue: number;
  unresolved: number;
  unowned: number;
  /** Open items the register has closed (ADR-0073). Absent from an older API response. */
  closed?: number;
  unmatchedEvents: string[];
  unmatchedParties: string[];
}

/**
 * The project's Open Items Register, counted (ADR-0073). The items themselves are read apart from the
 * other claims — a register of ~150 would otherwise own the panel — so this is what the panel shows.
 */
export interface OpenItemsSummaryView {
  total: number;
  answered: number;
  resolution: FactResolutionSummary;
}

/**
 * Whether a technical-review pass ASKED its protected-category question (#1379) — one per pass, the
 * newest row. `since` is how long the OUTCOME has held on every run recorded (kept across a change of
 * reason); `recordedAt` is when this row was written.
 */
export interface ReviewCoverageView {
  key: string;
  /**
   * Which review: `technical` — one row per protected pass, with its category — or `design`, ONE
   * run-level row (`design-review:run`, pass `run`, no category) saying whether the latest design
   * review landed: `ran`, or `not_run` (superseded, or no pass ran).
   */
  review: 'technical' | 'design';
  pass: string;
  /** The protected category a technical pass asks about. `null` exactly for the design review. */
  category: string | null;
  outcome: 'ran' | 'not_run' | 'not_asked';
  reason: string | null;
  since: string;
  recordedAt: string;
}

/**
 * The coverage as read: the rows the Hub could read, and a COUNT of those it could not. A malformed
 * row is never silently dropped — the panel says one could not be read (#1379 red-team).
 */
export interface ReviewCoverageReadView {
  rows: ReviewCoverageView[];
  unreadable: number;
}

export interface ProjectFactsView {
  facts: RecordFactView[];
  resolution: FactResolutionSummary | null;
  /** `null` = the server did not say (an older API, or a failed read) — never "no register". */
  openItems: OpenItemsSummaryView | null;
  /**
   * The technical review's coverage (#1379). `null` = the server did not say — never "every question
   * was asked". A row the Hub cannot read is dropped rather than guessed at.
   */
  reviewCoverage: ReviewCoverageReadView | null;
  /**
   * Design-review findings the server held back from this reader (canon "Where files go"; Brad,
   * 2026-10-27): a client reads no finding whose wording touches GGP's pricing. `null` = not said.
   */
  findingsWithheld: number | null;
}

const COVERAGE_OUTCOMES = new Set(['ran', 'not_run', 'not_asked']);

/** One server row as a {@link ReviewCoverageView}, or null when it is not one. */
function reviewCoverageOf(row: unknown): ReviewCoverageView | null {
  if (row === null || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  const v = r['value'];
  if (v === null || typeof v !== 'object') return null;
  const value = v as Record<string, unknown>;
  const { review, pass, category, outcome, reason, since } = value;
  const recordedAt = r['recordedAt'];
  if (
    typeof r['key'] !== 'string' ||
    typeof recordedAt !== 'string' ||
    typeof since !== 'string' ||
    typeof pass !== 'string' ||
    typeof outcome !== 'string' ||
    !COVERAGE_OUTCOMES.has(outcome) ||
    (reason !== null && typeof reason !== 'string')
  ) {
    return null;
  }
  // Each review in its own shape, under its own key: a technical pass (`technical-review:<pass>`)
  // names its category; the design review's ONE run-level row (`design-review:run`) names none and is
  // never `not_asked`. Anything else is unreadable, never guessed.
  let shaped: Pick<ReviewCoverageView, 'review' | 'category'>;
  const key = r['key'];
  if (
    review === 'technical' &&
    typeof category === 'string' &&
    key.startsWith('technical-review:') &&
    key.length > 'technical-review:'.length
  ) {
    shaped = { review, category };
  } else if (
    review === 'design' &&
    key === 'design-review:run' &&
    pass === 'run' &&
    category === null &&
    outcome !== 'not_asked'
  ) {
    shaped = { review, category: null };
  } else {
    return null;
  }
  return {
    key: r['key'],
    ...shaped,
    pass,
    outcome: outcome as ReviewCoverageView['outcome'],
    reason,
    since,
    recordedAt,
  };
}

/**
 * The Open Items Register as the record holds it (ADR-0073) — every item's CURRENT assertion, each
 * with the server's resolution, and the register's own summary (whose `unmatchedEvents` is the worklist
 * of milestones that would date the undated items).
 *
 * ⚠️ `loaded: false` is "could not be read", never "no register": a failed read that rendered as an
 * empty register would say the project has no open questions.
 */
export interface OpenItemsView {
  items: RecordFactView[];
  resolution: FactResolutionSummary | null;
  loaded: boolean;
}

/** An open pricing finding no register item names (ADR-0073 D5) — to CHECK, never an assertion. */
export interface CrosswalkGapView {
  findingKey: string;
  findingSummary: string;
  triage: string;
  status: string;
}

/** Every finding lands in exactly one of these; they sum to `findings`. */
export interface CrosswalkCountsView {
  findings: number;
  paired: number;
  gaps: number;
  closedOnly: number;
  housekeeping: number;
  notOpen: number;
  noStatus: number;
}

/**
 * The design-review crosswalk as the server computed it. `items` is what the findings were COMPARED
 * with: "no gaps" over an empty register is not "the two agree", and the panel must say which.
 */
export interface CrosswalkView {
  loaded: boolean;
  items: number;
  counts: CrosswalkCountsView;
  gaps: CrosswalkGapView[];
  closedCitations: { findingKey: string; itemKeys: string[] }[];
}

const NO_COUNTS: CrosswalkCountsView = {
  findings: 0,
  paired: 0,
  gaps: 0,
  closedOnly: 0,
  housekeeping: 0,
  notOpen: 0,
  noStatus: 0,
};

export async function getCrosswalk(code: string): Promise<CrosswalkView> {
  const failed: CrosswalkView = {
    loaded: false,
    items: 0,
    counts: NO_COUNTS,
    gaps: [],
    closedCitations: [],
  };
  try {
    const res = await request(`/projects/${encodeURIComponent(code)}/open-items/crosswalk`);
    if (!res.ok) return failed;
    const body = (await res.json()) as Partial<CrosswalkView>;
    // No counts from the server is a failure to read, never a zero: a panel of zeros would claim it
    // checked everything and found nothing.
    if (!body.counts) return failed;
    return {
      loaded: true,
      items: body.items ?? 0,
      counts: { ...NO_COUNTS, ...body.counts },
      gaps: Array.isArray(body.gaps) ? body.gaps : [],
      closedCitations: Array.isArray(body.closedCitations) ? body.closedCitations : [],
    };
  } catch {
    // A thrown fetch is a failure, never a clean crosswalk.
    return failed;
  }
}

/**
 * The schedule crosswalk (ADR-0069 N18): open items whose linked task's copied finish disagrees with
 * the master schedule as the record read it. `schedule` null = no master reading to compare against.
 */
export interface ScheduleCrosswalkView {
  loaded: boolean;
  items: number;
  /** `covers` of `of`: how many of the register's linked task names that schedule holds. */
  schedule: { filename: string; readAt: string; covers: number; of: number } | null;
  counts: {
    linked: number;
    unreadable: number;
    agree: number;
    disagree: number;
    notInSchedule: number;
    ambiguous: number;
  };
  disagreements: {
    itemKey: string;
    taskNumber: string;
    taskName: string;
    registerFinish: string;
    scheduleFinish: string;
  }[];
}

export async function getScheduleCrosswalk(code: string): Promise<ScheduleCrosswalkView> {
  const failed: ScheduleCrosswalkView = {
    loaded: false,
    items: 0,
    schedule: null,
    counts: { linked: 0, unreadable: 0, agree: 0, disagree: 0, notInSchedule: 0, ambiguous: 0 },
    disagreements: [],
  };
  try {
    const res = await request(
      `/projects/${encodeURIComponent(code)}/open-items/schedule-crosswalk`,
    );
    if (!res.ok) return failed;
    const body = (await res.json()) as Partial<ScheduleCrosswalkView>;
    // No counts is a failure to read, never a zero.
    if (!body.counts) return failed;
    return {
      loaded: true,
      items: body.items ?? 0,
      schedule: body.schedule ?? null,
      counts: { ...failed.counts, ...body.counts },
      disagreements: Array.isArray(body.disagreements) ? body.disagreements : [],
    };
  } catch {
    return failed;
  }
}

export async function getOpenItems(code: string): Promise<OpenItemsView> {
  const failed: OpenItemsView = { items: [], resolution: null, loaded: false };
  // ⚠️ A THROWN fetch, or a body that is not JSON, is a failure too — the `getCurrentSet` lesson. Left
  // unhandled the promise rejects and the panel sits on "Loading…" forever (red-team, 2026-09-29).
  let body: { facts?: RecordFactView[]; resolution?: FactResolutionSummary };
  try {
    const res = await request(`/projects/${encodeURIComponent(code)}/facts?kind=open_item`);
    if (!res.ok) return failed;
    body = (await res.json()) as typeof body;
  } catch {
    return failed;
  }
  return {
    items: Array.isArray(body.facts) ? body.facts : [],
    resolution: body.resolution ?? null,
    loaded: true,
  };
}

/** One priced issue a tracked quantity was compared against (A3 S8, the server's thresholds v1.1 check). Staff only. */
export interface TrackedQuantityAnchorView {
  issue: string;
  /** The priced issue's own value for this quantity, as the register recorded it. */
  anchorValue: string | null;
  result: {
    outcome: 'substantial' | 'within' | 'unchanged_mix' | 'changed_mix' | 'not_computed';
    reason?:
      'no_snapshot' | 'not_in_snapshot' | 'anchor_unread' | 'current_unread' | 'mix_shape_differs';
    detail?: string;
    anchor?: number | number[];
    current?: number | number[];
    delta?: number;
    deltaPct?: number | null;
    band?: number;
  };
}

export interface TrackedQuantityRowView {
  key: string;
  q: string;
  current: string | null;
  outcome:
    'substantial' | 'within' | 'reviewer_judges' | 'no_band' | 'not_canon_class' | 'not_computed';
  /** Every priced issue, in the register's order. Substantial against any; within only when the last is. */
  anchors: TrackedQuantityAnchorView[];
  registerFlag: 'substantial' | 'not_substantial' | 'unread';
  disagreesWithRegister: boolean;
}

export interface TrackedQuantityCheckView {
  registerThresholds: string | null;
  thresholds: string;
  pricedAnchors: string[];
  rows: TrackedQuantityRowView[];
  missingRows: string[];
  droppedRows: number;
}

/**
 * The record's Tracked Quantities table, checked by the server against the latest priced issue.
 * `check: null` with `loaded: true` is a record with no table; `loaded: false` is a failure, never an
 * empty table.
 */
export async function getTrackedQuantityCheck(
  code: string,
): Promise<{ loaded: boolean; check: TrackedQuantityCheckView | null }> {
  try {
    const res = await request(`/projects/${encodeURIComponent(code)}/facts?kind=tracked_quantity`);
    if (!res.ok) return { loaded: false, check: null };
    const body = (await res.json()) as { check?: TrackedQuantityCheckView | null };
    return { loaded: true, check: body.check ?? null };
  } catch {
    return { loaded: false, check: null };
  }
}

/**
 * Pin WHICH GGP proforma governs a project (ADR-0069 N32), by Nexus document id, as a `deal_fact` on
 * the record. OWNER only (`basis:lock`; Brad 2026-10-08 — unlike the other deal-fact pins, which an
 * admin may set) — the server enforces it; the page only decides
 * what to draw. The name on the record comes from the session, never from this body. Written
 * reasoning is required (canon R9). Takes effect on the project's NEXT run; this run is not rewritten.
 */
export async function pinGoverningProforma(
  code: string,
  /** The document that governs, or `null` to RELEASE the pin (the documents decide again). */
  documentId: string | null,
  reason: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await request(`/projects/${encodeURIComponent(code)}/facts`, {
    method: 'POST',
    body: JSON.stringify({
      kind: 'deal_fact',
      key: 'governingProforma',
      value: { field: 'governingProforma', value: documentId, reason },
      source: 'project team',
      arrivalPath: 'analysis view: governing proforma',
      tier: 'stated',
    }),
  });
  if (res.ok) return { ok: true };
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  return {
    ok: false,
    error: typeof body.error === 'string' ? body.error : `could not pin (${res.status})`,
  };
}

/**
 * Set or release a project's own yield-on-cost floor (ADR-0069 A8). Owner only on the server
 * (`basis:lock`), and only stricter than GGP's 5.75%: the server refuses anything lower. `floor` is a
 * RATIO (0.065 = 6.5%), or `null` to release (GGP's floor governs again). Takes effect on the next run.
 */
export async function setProjectYieldFloor(
  code: string,
  floor: number | null,
  reason: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const res = await request(`/projects/${encodeURIComponent(code)}/facts`, {
    method: 'POST',
    body: JSON.stringify({
      kind: 'deal_fact',
      key: 'yieldOnCostFloor',
      value: { field: 'yieldOnCostFloor', value: floor, reason },
      source: 'project team',
      arrivalPath: 'project page: yield-on-cost floor',
      tier: 'stated',
    }),
  });
  if (res.ok) return { ok: true };
  const body = (await res.json().catch(() => ({}))) as { error?: unknown };
  return {
    ok: false,
    error: typeof body.error === 'string' ? body.error : `could not set the floor (${res.status})`,
  };
}

/**
 * The record's current facts, newest first, with the server's resolution.
 *
 * ⚠️ Degrades to `[]` / `null` on 404/401 rather than throwing — but a null summary must render as
 * "not available", never as a set of zeroes. Zero unresolved items and no answer at all are opposite
 * facts, and the panel says so.
 */
export async function getProjectFacts(code: string): Promise<ProjectFactsView> {
  const res = await request(`/projects/${encodeURIComponent(code)}/facts`);
  if (!res.ok) {
    return {
      facts: [],
      resolution: null,
      openItems: null,
      reviewCoverage: null,
      findingsWithheld: null,
    };
  }
  const body = (await res.json()) as {
    facts?: RecordFactView[];
    resolution?: FactResolutionSummary;
    openItems?: OpenItemsSummaryView;
    reviewCoverage?: unknown;
    findingsWithheld?: unknown;
  };
  return {
    facts: Array.isArray(body.facts) ? body.facts : [],
    resolution: body.resolution ?? null,
    openItems: body.openItems ?? null,
    findingsWithheld:
      typeof body.findingsWithheld === 'number' && Number.isInteger(body.findingsWithheld)
        ? body.findingsWithheld
        : null,
    reviewCoverage: Array.isArray(body.reviewCoverage)
      ? (() => {
          const read = body.reviewCoverage.map(reviewCoverageOf);
          return {
            rows: read.filter((v): v is ReviewCoverageView => v !== null),
            unreadable: read.filter((v) => v === null).length,
          };
        })()
      : null,
  };
}

/**
 * Clear a variance (owner/admin only, server-enforced). Clearing an open Stop&Fix unfreezes the CRP-1
 * release. Throws {@link CrpForbiddenError} on 403 and {@link CrpConflictError} on 409 (already cleared),
 * reusing the CRP error types since the governance semantics + UI handling are identical.
 *
 * The `rationale` is invited, never required (canon `said()`, #1155): a blank one is recorded as "no note
 * given", not dressed up as a reason.
 */
export async function clearVariance(
  code: string,
  id: string,
  rationale?: string | null,
  action: 'clear' | 'write-off' = 'clear',
): Promise<void> {
  const verb = action === 'clear' ? 'clear' : 'write off';
  const res = await request(
    `/projects/${encodeURIComponent(code)}/variances/${encodeURIComponent(id)}/${action}`,
    { method: 'POST', body: JSON.stringify({ rationale: rationale ?? null }) },
  );
  if (res.status === 403) {
    throw new CrpForbiddenError(`Only an owner or admin can ${verb} a variance.`);
  }
  if (res.status === 409) {
    throw new CrpConflictError('This variance has already been cleared.');
  }
  if (!res.ok)
    throw new Error(`We couldn’t ${verb} the variance just now. Give it another try in a moment.`);
}

/** The two reviews a record can be put through on demand (ADR-0060 D4, ADR-0063 D5 step 3). */
export type ReviewKind = 'design-review' | 'technical-review';

/**
 * Start a review — `POST /projects/:code/{design,technical}-review`. The route only ENQUEUES (202);
 * the worker reads the current set and lands what survives verification as `stated` findings.
 * Refusals come back in the server's words, never as a throw.
 */
export async function startReview(
  code: string,
  kind: ReviewKind,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await request(`/projects/${encodeURIComponent(code)}/${kind}`, { method: 'POST' });
  if (res.status === 202) return { ok: true };
  let error: string | undefined;
  try {
    error = ((await res.json()) as { error?: string }).error;
  } catch {
    // A non-JSON refusal still gets a message below.
  }
  if (res.status === 403) return { ok: false, message: 'A viewer cannot start a review.' };
  return {
    ok: false,
    message: error ?? 'We couldn’t start the review just now. Give it another try.',
  };
}

/**
 * Confirm or reject a protected-stop candidate (ADR-0063 D3) — `POST /projects/:code/findings/:key/attest`.
 *
 * ⚠️ `factId` IS THE ASSERTION THE OWNER READ. The route refuses (409 `stale-finding`) when the finding
 * gained a newer row in between, so a decision is never silently applied to text nobody reviewed.
 * Every refusal comes back as a result, never a throw: the owner needs to read WHY in the panel.
 */
export type AttestResult =
  | { kind: 'ok'; outcome: 'confirm' | 'reject' }
  | { kind: 'stale' }
  | { kind: 'refused'; message: string };

export async function attestFinding(
  code: string,
  key: string,
  body: { outcome: 'confirm' | 'reject'; factId: string; rationale?: string | null },
): Promise<AttestResult> {
  const res = await request(
    `/projects/${encodeURIComponent(code)}/findings/${encodeURIComponent(key)}/attest`,
    {
      method: 'POST',
      body: JSON.stringify({
        outcome: body.outcome,
        factId: body.factId,
        rationale: body.rationale?.trim() ? body.rationale.trim() : null,
      }),
    },
  );
  if (res.ok) return { kind: 'ok', outcome: body.outcome };
  let parsed: { error?: string; refusal?: string } = {};
  try {
    parsed = (await res.json()) as { error?: string; refusal?: string };
  } catch {
    // A non-JSON refusal (a rate-limit body) still gets a message below.
  }
  if (res.status === 409 && parsed.refusal === 'stale-finding') return { kind: 'stale' };
  if (res.status === 403) {
    return {
      kind: 'refused',
      message: 'Only a tenant owner can decide a protected-stop candidate.',
    };
  }
  return {
    kind: 'refused',
    message: parsed.error ?? 'We couldn’t record that decision just now. Give it another try.',
  };
}

// ── Challenge-and-rescore — the Human Decision Point (ADR-0038; canon R1–R9) ────────────────────────
// Every scorecard finding is challengeable by a named person, every challenge is answered with recorded
// reasoning, and an accepted correction re-scores in the open. These mirror packages/api/src/routes/
// challenges.ts and stay PERMISSIVE where the wire is (this package imports no @ggpathos/*).

/** What a challenge contests: one scorecard finding, or the scoring standard itself. */
export type ChallengeKind = 'finding' | 'standard';
/** How a challenge was answered (R4). `partial` accepts some of the claim. */
export type ChallengeResolution = 'accepted' | 'rejected' | 'partial';
/** The tier a correction records at (R6): backed by a document, or on a named person's word. */
export type ProvenanceTier = 'verified' | 'disclosed';

/** One category whose score moved under an accepted correction. */
export interface CategoryDelta {
  category: string;
  priorScore: number;
  newScore: number;
}

/** The visible result of a re-score (R5) — prior, new, the delta, and every finding that moved. */
export interface Rescore {
  priorScore: number;
  newScore: number;
  delta: number;
  priorRecommendation?: Verdict;
  newRecommendation?: Verdict;
  recommendationChanged?: boolean;
  changedCategories?: CategoryDelta[];
  /**
   * #1383 / #1415 — present ONLY when the correction changed WHICH DSCR floor applies, so the engine
   * re-resolved it for the corrected deal: `{ resolution, state: 'recorded' | 'not-recorded' }`. Typed
   * `unknown` on purpose: it is stored jsonb served as-is, so read it with {@link correctedDscrFloorOf},
   * never by shape-guessing at a call site — a malformed value must render nothing, not throw.
   */
  correctedDscrFloor?: unknown;
}

/** The DSCR floor a re-score held the CORRECTED deal to (#1415), as the engine resolved it. */
export interface CorrectedDscrFloorView {
  floor: number;
  basis: 'state-agency' | 'national-default' | 'market-rate';
  /** Two-letter code the resolution read, or null (always null on the market-rate basis). */
  state: string | null;
  /** The agency whose floor applies; null unless the basis is `state-agency`. */
  agency: string | null;
  /** The engine's plain-language provenance — client copy (no sheet citation). */
  label: string;
  /**
   * Whether the state the floor was resolved at was RECORDED on the run. `not-recorded` — a run
   * persisted before the asserted state was stamped, so a tax-credit floor fell to the national default.
   */
  stateOnRun: 'recorded' | 'not-recorded';
}

const FLOOR_BASES = new Set(['state-agency', 'national-default', 'market-rate']);

/** The re-score's corrected DSCR floor, or null when it carries none or it cannot be read (#1415). */
export function correctedDscrFloorOf(r: Rescore): CorrectedDscrFloorView | null {
  const c = r.correctedDscrFloor;
  if (c === null || typeof c !== 'object') return null;
  const { resolution, state: stateOnRun } = c as Record<string, unknown>;
  if (stateOnRun !== 'recorded' && stateOnRun !== 'not-recorded') return null;
  if (resolution === null || typeof resolution !== 'object') return null;
  const { floor, basis, state, agency, label } = resolution as Record<string, unknown>;
  if (
    typeof floor !== 'number' ||
    !Number.isFinite(floor) ||
    typeof basis !== 'string' ||
    !FLOOR_BASES.has(basis) ||
    (state !== null && typeof state !== 'string') ||
    (agency !== null && typeof agency !== 'string') ||
    typeof label !== 'string' ||
    label.trim() === ''
  ) {
    return null;
  }
  return {
    floor,
    basis: basis as CorrectedDscrFloorView['basis'],
    state,
    agency,
    label,
    stateOnRun,
  };
}

/**
 * A delta exists on this answer but is not this deal's history, so the server withheld it (ADR-0038 R7).
 * Present INSTEAD of `rescore`, never alongside it — the two are mutually exclusive by construction.
 */
export interface RescoreWithheld {
  /** `superseded` — the stored prior disagrees with the issued score. `unverifiable` — no issued score. */
  reason: 'superseded' | 'unverifiable';
  claimedPriorScore: number;
  issuedScore?: number;
}

export interface ChallengeAnswer {
  id: string;
  resolution: ChallengeResolution;
  reasoning: string;
  answeredByName: string;
  provenanceTier: ProvenanceTier | null;
  rescore: Rescore | null;
  rescoreWithheld?: RescoreWithheld;
  answeredAt: string;
}

/** A challenge on the record, with its answer when it has one. `answer: null` IS the open state (R4). */
export interface Challenge {
  id: string;
  kind: ChallengeKind;
  target: string;
  claim: string;
  challengerName: string;
  standardVersion: string;
  supportingDocumentId: string | null;
  createdAt: string;
  status: 'open' | 'answered';
  answer: ChallengeAnswer | null;
}

export interface ChallengeLedger {
  challenges: Challenge[];
  openCount: number;
}

/** A correction an accepted challenge applies — a wrong tier, or a wrong figure in the deal. */
export type ChallengeCorrection =
  | { kind: 'selection'; category: string; selection: { points: number; evidence: string } }
  | { kind: 'deal'; patch: Record<string, number | boolean | string> };

/** Raised when the session is valid but the role is not permitted to answer (403). */
export class ChallengeForbiddenError extends Error {}
/** Raised when the challenge is already answered, or the run cannot be re-scored (409). */
export class ChallengeConflictError extends Error {}
/** Raised when the request itself is refused — e.g. a standard challenge cannot re-score a deal (400). */
export class ChallengeRefusedError extends Error {}

/** The challenge ledger for a run. Returns an empty ledger rather than throwing — it is a side panel. */
export async function getChallenges(analysisId: string): Promise<ChallengeLedger> {
  const res = await request(`/analyses/${encodeURIComponent(analysisId)}/challenges`);
  if (!res.ok) return { challenges: [], openCount: 0 };
  const body = (await res.json()) as Partial<ChallengeLedger>;
  const challenges = Array.isArray(body.challenges) ? body.challenges : [];
  return { challenges, openCount: body.openCount ?? challenges.filter((c) => !c.answer).length };
}

/**
 * File a challenge. The challenger's NAME is not sent — the server takes it from the session, so the
 * record names who actually acted rather than whoever the page felt like claiming (ADR-0038 R2).
 */
export async function fileChallenge(
  analysisId: string,
  input: {
    kind: ChallengeKind;
    target: string;
    claim: string;
    supportingDocumentId?: string | null;
  },
): Promise<Challenge> {
  const res = await request(`/analyses/${encodeURIComponent(analysisId)}/challenges`, {
    method: 'POST',
    body: JSON.stringify({
      kind: input.kind,
      target: input.target,
      claim: input.claim,
      supportingDocumentId: input.supportingDocumentId ?? null,
    }),
  });
  if (res.status === 409) {
    throw new ChallengeConflictError('This deal has no scorecard to challenge yet.');
  }
  if (!res.ok) {
    throw new Error('We couldn’t file that challenge just now. Give it another try in a moment.');
  }
  return (await res.json()) as Challenge;
}

/**
 * The refusal the SERVER wrote, rather than a guess made here.
 *
 * `POST …/answer` has four distinct 409s — already answered, predates re-scoring, no issued score, and
 * the R7 reproduction gate — and each one names a different remedy ("answer it without a correction",
 * "re-run the deal"). The irreproducible branch additionally sends `issuedScore` and `reproducedScore`
 * precisely so the reader can see WHY the tool declined instead of only THAT it declined (ADR-0038 R7).
 * Collapsing them into one client-side string threw all of that away at the last hop.
 *
 * Falls back to a generic line only when the body is missing or unreadable — never invents a cause.
 */
async function challengeConflictMessage(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as {
    error?: unknown;
    issuedScore?: unknown;
    reproducedScore?: unknown;
  } | null;
  const reason = typeof body?.error === 'string' && body.error.trim() ? body.error.trim() : null;
  if (!reason) return 'That answer can’t be recorded right now.';
  const sentence = `${reason.charAt(0).toUpperCase()}${reason.slice(1)}.`;
  // Both numbers or neither: one score alone is not a comparison, and half a disclosure reads as a fact.
  if (typeof body?.issuedScore === 'number' && typeof body?.reproducedScore === 'number') {
    return `${sentence} It scored ${body.issuedScore} when issued, and ${body.reproducedScore} replayed under today’s standard.`;
  }
  return sentence;
}

/**
 * Answer a challenge (owner/admin, server-enforced). Reasoning is REQUIRED — the anti-rubber-stamp rule
 * is enforced at the route and at the database, so an empty box is never accepted here either (R9).
 */
export async function answerChallenge(
  analysisId: string,
  challengeId: string,
  input: {
    resolution: ChallengeResolution;
    reasoning: string;
    correction?: ChallengeCorrection | null;
  },
): Promise<{ rescore: Rescore | null }> {
  const res = await request(
    `/analyses/${encodeURIComponent(analysisId)}/challenges/${encodeURIComponent(challengeId)}/answer`,
    {
      method: 'POST',
      body: JSON.stringify({
        resolution: input.resolution,
        reasoning: input.reasoning,
        ...(input.correction ? { correction: input.correction } : {}),
      }),
    },
  );
  if (res.status === 403) {
    throw new ChallengeForbiddenError('Only an owner or admin can answer a challenge.');
  }
  if (res.status === 409) {
    throw new ChallengeConflictError(await challengeConflictMessage(res));
  }
  if (res.status === 400) {
    throw new ChallengeRefusedError(
      'That answer can’t be recorded as written — check the reasoning and the correction.',
    );
  }
  if (!res.ok) {
    throw new Error('We couldn’t record that answer just now. Give it another try in a moment.');
  }
  return (await res.json()) as { rescore: Rescore | null };
}

// ── The standards write-back queue (ADR-0038 F5; canon R7 / Ground Truth §11.3) ────────────────────

/** One entry in the write-back queue — a challenge against the STANDARD, not against a deal. */
export interface WriteBackItem {
  id: string;
  analysisId: string;
  target: string;
  claim: string;
  challengerName: string;
  /** The standard version the argument was made against. */
  raisedAgainstVersion: string;
  /** Whether the standard has moved since. A fact about versions — NOT a claim it was addressed. */
  standardMovedSince: boolean;
  supportingDocumentId: string | null;
  createdAt: string;
  status: 'open' | 'answered';
  resolution: ChallengeResolution | null;
  reasoning: string | null;
  answeredByName: string | null;
  answeredAt: string | null;
}

export interface WriteBackQueue {
  currentStandardVersion: string;
  items: WriteBackItem[];
  counts: { open: number; agreed: number; declined: number };
}

/** The tenant's standards queue, across every deal. Empty rather than throwing — it is a working list. */
export async function getWriteBackQueue(): Promise<WriteBackQueue | null> {
  const res = await request('/standards/write-back');
  if (!res.ok) return null;
  return (await res.json()) as WriteBackQueue;
}

// ── Membership administration (ADR-0055 D4) ──────────────────────────────────

export interface PlatformHealthSnapshot {
  checkedAt: string;
  windowHours: number;
  /** What the counts COVER — this tenant's runs, never a cross-tenant rollup (ADR-0064 D3). */
  scope: 'tenant';
  database: 'ok';
  runs: {
    queued: number;
    running: number;
    awaitingIndex: number;
    succeeded: number;
    failed: number;
    canceled: number;
    unclassifiedFailures: number;
  };
  /**
   * Runs in the window the Nexus seam broke — this tenant's, from its own run records.
   *
   * ⚠️ OPTIONAL on the client though the api always sends it: during a deploy a newer SPA can meet an
   * older api, and an absent field must render `unknown`, not crash the page (red-team, row G).
   */
  seam?: { failedRuns: number; timeouts: number };
  /**
   * Runs that failed at the `extract` stage — no extractable text, or (#1196) an extractor that threw.
   * Optional for the same reason as `seam`.
   */
  extraction?: { failedRuns: number };
  /**
   * Alarms firing now. PLATFORM-wide, unlike every other number here. `null` means they could not be
   * read — which is NOT the same as "none firing" (`firing: []`), and must never render as it.
   */
  alarms: { scope: 'platform'; firing: readonly string[] } | null;
  /** The signals that could not be read this time. */
  unavailable: readonly 'alarms'[];
}

export type PlatformHealthResult =
  { kind: 'ok'; snapshot: PlatformHealthSnapshot } | { kind: 'forbidden' } | { kind: 'error' };

export async function getPlatformHealth(): Promise<PlatformHealthResult> {
  try {
    const res = await request('/admin/platform-health');
    if (res.status === 403) return { kind: 'forbidden' };
    if (!res.ok) return { kind: 'error' };
    return { kind: 'ok', snapshot: (await res.json()) as PlatformHealthSnapshot };
  } catch {
    return { kind: 'error' };
  }
}

export type MemberRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface TenantMember {
  userId: string;
  displayName: string | null;
  role: MemberRole;
  status: 'active' | 'invited' | 'disabled';
  /**
   * Whether this member may mint an MCP credential (ADR-0033 / ADR-0057 D1). Default DENY, and NOT
   * implied by role — an owner without it cannot mint either.
   */
  canUseMcp: boolean;
}

/**
 * The member list, or `forbidden` when the caller may not see it.
 *
 * ⚠️ A 403 is NOT folded into an empty list, unlike `getProjects` above. "Nobody has access to this
 * tenant" and "you may not see who has access" are different facts, and a page that renders the
 * second as the first tells an admin their tenant is empty.
 */
export type MembersResult =
  { kind: 'ok'; members: TenantMember[] } | { kind: 'forbidden' } | { kind: 'error' };

export async function getTenantMembers(): Promise<MembersResult> {
  try {
    const res = await request('/admin/members');
    if (res.status === 403) return { kind: 'forbidden' };
    if (!res.ok) return { kind: 'error' };
    const body = (await res.json()) as { members?: TenantMember[] };
    return { kind: 'ok', members: Array.isArray(body.members) ? body.members : [] };
  } catch {
    return { kind: 'error' };
  }
}

/**
 * The refusals the server can return, carried through by NAME.
 *
 * 🔴 These are the escalation guards (no self-edit, owner-is-owner-only, the last owner stays). Each
 * is a specific, actionable thing the person did — "promote another owner first" is a next step;
 * "something went wrong" is not. Collapsing them into a generic failure would throw away the only
 * part of the response worth reading.
 */
export type MemberUpdateResult =
  | { kind: 'ok'; member: { userId: string; role: MemberRole; status: string; canUseMcp: boolean } }
  | { kind: 'refused'; refusal: string; message: string }
  | { kind: 'forbidden' }
  | { kind: 'error' };

export async function updateTenantMember(
  userId: string,
  // ⚠️ `canUseMcp` is optional and BOOLEAN, not a flag: `false` is a revocation, and it is the half
  // that matters under incident conditions. Anything that treats it as truthy drops it silently.
  change: { role?: MemberRole; status?: 'active' | 'disabled'; canUseMcp?: boolean },
): Promise<MemberUpdateResult> {
  try {
    const res = await request(`/admin/members/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(change),
    });
    if (res.status === 403) return { kind: 'forbidden' };
    if (res.status === 409) {
      const body = (await res.json()) as { refusal?: string; error?: string };
      return {
        kind: 'refused',
        refusal: body.refusal ?? 'refused',
        message: body.error ?? 'that change was refused',
      };
    }
    if (!res.ok) return { kind: 'error' };
    const member = (await res.json()) as {
      userId: string;
      role: MemberRole;
      status: string;
      canUseMcp: boolean;
    };
    return { kind: 'ok', member };
  } catch {
    return { kind: 'error' };
  }
}

/**
 * One recorded change to who may reach this tenant (ADR-0055 D4, migration 0046).
 *
 * ⚠️ Names, not ids, are what render. The server stamps them at write time because the app role
 * cannot resolve a user id to a person — so the name IS the record, and a row whose name is missing
 * is one written before that person had one, never one to look up later.
 */
export interface MembershipEvent {
  id: string;
  actorName: string | null;
  targetName: string | null;
  fromRole: string | null;
  toRole: string | null;
  toStatus: string | null;
  /**
   * The MCP capability AFTER the change (ADR-0057 D1). One side is enough, and only one is served:
   * the database's `capability_both_sides_check` makes a non-NULL value mean the capability moved, so
   * `true` reads as a grant and `false` as a revocation. NULL means it did not move.
   */
  toCanUseMcp: boolean | null;
  createdAt: string;
}

export async function getMembershipEvents(): Promise<MembershipEvent[]> {
  try {
    const res = await request('/admin/members/events');
    if (!res.ok) return [];
    const body = (await res.json()) as { events?: MembershipEvent[] };
    return Array.isArray(body.events) ? body.events : [];
  } catch {
    return [];
  }
}
